import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import html2canvas from 'html2canvas';
import {
    Box, Typography, TextField, InputAdornment, ToggleButton,
    ToggleButtonGroup, Button, Snackbar, Collapse, Divider, IconButton,
    Checkbox, FormControlLabel, Select, MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import TuneIcon from '@mui/icons-material/Tune';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import {
    generateCandidates, calcSaving, calcC2Bill, billToKwh, kwhToBill,
    parseInverterKw, YIELD_PER_KWP, BATT_RTE, BATT_DOD,
    computeSafetyRatio, SEDA_CAP_1P, SEDA_CAP_3P,
} from '../../../hooks/useRscPricing';
import { getAddons } from '../../../api/resourceApi';
import { rankPlans } from './roiRanking';
import { buildRoiSearcherMsg } from './advancedWhatsApp';
import { WA_TEMPLATE_PLACEHOLDERS, DEFAULT_WA_TEMPLATE, loadWaTemplate, saveWaTemplateStr, fillWaTemplate } from '../waTemplate';
import TenYearComparisonModal, { TenYearComparisonBody } from './tenYearComparisonModal';

/**
 * RSC Advanced — ROI Searcher (System Recommendation layout).
 *
 * Pricing / promo / condition / rebate MECHANISM is IDENTICAL to the Simple RSC
 * calculator, because this view uses the SHARED engine + SHARED state (NOT a local
 * copy of the math):
 *   - `phaseMode` / `setPhaseMode` come from the parent (the engine closes over them).
 *   - `promoBackupbox/First640/PerBatt/GovRebate` (+ setters) come from the parent
 *     (the engine's computePromoAmt / buildBreakdown / getPrice close over them).
 *   - So `engine.getPrice(pkg,'cash',disc)`, `engine.buildBreakdown(pkg,disc,'cash')`,
 *     and `engine.computePromoAmt(pkg)` yield BYTE-IDENTICAL results to Simple.
 *
 * Pricing per plan card mirrors Simple's Bill-tab option card EXACTLY:
 *   const disc      = { promoAmt: engine.computePromoAmt(pkg), discAmt: 0, discPct: 0 };
 *   const finalCash = engine.getPrice(pkg, 'cash', disc) + addOns;   // after ALL promos (incl. SuRIA)
 *   const govRebate = promoGovRebate ? calcGovRebate(invKwOf(pkg)) : 0;
 *   const preRebate = finalCash + govRebate;                          // the QUOTE shown to the customer
 *   CC tiles        = engine.ccFromCash(preRebate, term);            // SuRIA claimed from TNB after install
 * The headline Cash + the CC tiles convert the PRE-rebate quote; an "investment"
 * (after-rebate) line is shown beneath, exactly like Simple's option card.
 *
 * Advanced ONLY differs in DISPLAY + adds steppers (battery / panels / inverter),
 * ROI-search ranking (rankPlans → 3 picks), a Customer-Profile grid, a GLOBAL
 * add-on catalog picker (Simple's method), the 10-year modal and WhatsApp export.
 *
 * Props (forwarded by AdvancedMode from the Simple calculator):
 *   { engine, atapPrices, batteryOptions, t, poppins, toggleSx, lang,
 *     phaseMode, setPhaseMode,
 *     promoBackupbox, setPromoBackupbox, promoFirst640, setPromoFirst640,
 *     promoPerBatt, setPromoPerBatt, promoGovRebate, setPromoGovRebate,
 *     invKwOf, calcGovRebate }
 * engine = { getBasePrice, getPrice, computePromoAmt, cardPctBase, buildBreakdown, ccFromCash }
 */

const CC_TERMS = [12, 24, 36, 60];
const SCENARIO_STEPS = [-300, -200, -100, 0, 100, 200, 300];

// ── Add-on catalog helpers — COPIED VERBATIM from residentialSolarCalculatorV2.jsx
//    (Simple RSC). Do NOT change the formula: the Advanced view must compute every
//    catalog add-on EXACTLY the way Simple does so a hand-off freezes identical
//    numbers. computeAddonAmount honours the "base+rate" syntax (e.g. "700+170");
//    resolveAddonQty resolves PerPanel → panel count, else 1; addonMatchesPhase /
//    isCommercialAddon drive the picker's option filter. ──
function computeAddonAmount(addon, qty) {
    if (!addon) return { base: 0, perUnit: 0, total: 0 };
    const raw = String(addon.price ?? '');
    const cleaned = raw.replace(/[^\d.+]/g, '');
    // A "+" in the price is an unambiguous base+rate signal ("700+170" = RM 700 setup
    // + RM 170 per unit). Honor it FIRST, regardless of unitType — otherwise, if the
    // row's unitType is ever missing/stale at compute time (it has been, intermittently),
    // the old fallback stripped the "+" and concatenated the digits → "700170". Parsing
    // the "+" up front makes that concatenation bug impossible. (Bug fix 2026-06-01.)
    if (cleaned.includes('+')) {
        const [b, p] = cleaned.split('+');
        const base = parseFloat(b) || 0;
        const perUnit = parseFloat(p) || 0;
        return { base, perUnit, total: base + perUnit * (Number(qty) || 0) };
    }
    const qtyBearing = addon.unitType === 'PerPanel' || addon.unitType === 'PerItem' || addon.unitType === 'PerYear';
    if (qtyBearing) {
        const perUnit = parseFloat(cleaned) || 0;
        return { base: 0, perUnit, total: perUnit * (Number(qty) || 0) };
    }
    // 'None' or anything else — treat as a literal one-shot price.
    const num = parseFloat(raw.replace(/[^\d.]/g, '')) || 0;
    return { base: 0, perUnit: num, total: num };
}

// Single source of truth for an add-on's qty — an explicit `_qty` on the row wins
// for EVERY add-on; otherwise PerPanel defaults to the panel count, and anything
// else is a one-shot of 1. computeAddonAmount honors base+rate regardless of
// unitType, so resolving qty here fixes it for ALL qty-bearing add-ons. (VERBATIM
// from Simple's resolveAddonQty — the editable global rows carry a `_qty`.)
function resolveAddonQty(addon, pkgPanels) {
    if (addon && addon._qty != null && addon._qty !== '' && Number.isFinite(Number(addon._qty))) {
        return Math.max(0, Number(addon._qty));
    }
    if (addon && addon.unitType === 'PerPanel') return Math.max(0, Number(pkgPanels) || 0);
    return 1;
}

// Phase inference for the catalog picker filter (verbatim from Simple).
function inferPhaseFromName(name) {
    const n = String(name || '').toLowerCase();
    if (/(?:three\s*phase|3\s*phase|\b3p\b|\btp\b|tp[_-]|_3p[_-]?|three[_-]?phase)/i.test(n)) return 'Three Phase';
    if (/(?:single\s*phase|1\s*phase|\b1p\b|\bsp\b|sp[_-]|_1p[_-]?|single[_-]?phase)/i.test(n)) return 'Single Phase';
    return null;
}
function effectivePhase(a) {
    const ex = a && a.phase;
    if (ex && ex !== 'Any') return ex;
    const inferred = inferPhaseFromName((a && (a.itemName || a.name)) || '');
    return inferred || 'Any';
}
function addonMatchesPhase(a, phaseLabel) {
    const ep = effectivePhase(a);
    return ep === 'Any' || ep === phaseLabel;
}
function isCommercialAddon(a) {
    if (!a) return false;
    return /commercial/i.test(a.category || '') || /commercial/i.test(a.itemName || a.name || '');
}

// Inverter-upgrade add-on name pattern — same shape RSC V2 uses (walkInvChain /
// INV_UP_RE): "inverter upgrade 1p5 to 1p8". Captures from/to phase + kW.
const INV_UP_RE = /inverter\s*upgrade\s*(\d)\s*p\s*(\d+(?:\.\d+)?)\s*to\s*(\d)\s*p\s*(\d+(?:\.\d+)?)/i;
// Quick-add shortcut add-ons (mirror Simple's ADDON_SHORTCUTS) — matched by name.
const ADDON_SHORTCUTS = [
    { key: 'hacking',   re: /hacking/i,                 label: 'Hacking',   label_cn: '凿墙',   label_my: 'Hacking' },
    { key: 'concrete',  re: /concrete\s*installation/i, label: 'Concrete',  label_cn: '混凝土', label_my: 'Konkrit' },
    { key: 'optimizer', re: /optimi[sz]er/i,            label: 'Optimizer', label_cn: '优化器', label_my: 'Optimizer' },
];

// Parse an add-on price string into a flat RM (supports the "base+rate" syntax,
// e.g. "250+0"). Inverter upgrades are flat one-shots so qty is irrelevant; we
// take base + perUnit. Mirrors computeAddonAmount(addon,1).total in RSC V2.
function addonAmount(addon) {
    if (!addon) return 0;
    const cleaned = String(addon.price ?? '').replace(/[^\d.+]/g, '');
    if (cleaned.includes('+')) {
        const [b, p] = cleaned.split('+');
        return (parseFloat(b) || 0) + (parseFloat(p) || 0);
    }
    return parseFloat(cleaned) || 0;
}

// generateCandidates reads its rows via the module's casing-tolerant `n()` helper:
//   cashPrice/CashPrice, cc36Price/CC36Price, cash3p8k/Cash3p8k, cc36_3p8k/CC36_3p8k,
//   plus row.energy (kWp), row.panelQty, row.inverterSize / row.inverterSize3p.
// atapPrices uses {kwp, panels, cash, cc, cash3p, cc36_3p, inverter, inverter3p}.
function atapToPricingRow(p) {
    return {
        energy: p.kwp,
        panelQty: p.panels,
        cashPrice: p.cash,
        cc36Price: p.cc,
        cash3p8k: p.cash3p,
        cc36_3p8k: p.cc36_3p,
        inverterSize: p.inverter,
        inverterSize3p: p.inverter3p,
    };
}

// Monthly energy-flow for the details metrics (New Import / New Export / Battery
// Utilisation). Reuses the engine's own constants (YIELD_PER_KWP / BATT_RTE /
// BATT_DOD) so the flow matches what calcSaving computes internally — no new math.
function energyFlow(kwp, usageKwh, battKwhTotal, dayPct) {
    const monthlyGen = kwp * YIELD_PER_KWP * 30;
    const dayUsage = usageKwh * dayPct;
    const nightUsage = usageKwh - dayUsage;
    const storage = battKwhTotal * BATT_DOD * 30;
    const solarAfter = monthlyGen - dayUsage;
    const battIn = solarAfter > 0 ? Math.min(storage, solarAfter) : 0;
    const battOut = battIn * BATT_RTE;
    const exportKwh = Math.max(0, solarAfter - battIn);
    const newImportRaw = solarAfter < 0
        ? Math.max(0, nightUsage - battOut) - solarAfter
        : Math.max(0, nightUsage - battOut);
    const newImport = Math.round(newImportRaw);
    const exportRnd = Math.round(exportKwh);
    const batteryUtil = storage > 0 ? Math.min(100, Math.round(battOut / storage * 1000) / 10) : null;
    return { newImport, exportKwh: exportRnd, batteryUtil, battOut, storage };
}

// sessionStorage key for Advanced mode's OWN inputs (bill/usage/day%/panel-limit,
// advanced settings, selections). Shared phase/promo state persists via Simple's
// parent draft, so it's not duplicated here. Mirrors Simple's RSC_DRAFT_KEY pattern.
const ADV_DRAFT_KEY = 'rsc_adv_draft_v1';

export default function RoiSearcherView({
    engine, atapPrices = [], batteryOptions = [], t, poppins, toggleSx, lang,
    // SHARED phase + promo state (the engine closes over these) + display helpers.
    phaseMode, setPhaseMode,
    promoBackupbox, setPromoBackupbox,
    promoFirst640, setPromoFirst640,
    promoPerBatt, setPromoPerBatt,
    promoGovRebate, setPromoGovRebate,
    invKwOf, calcGovRebate,
    // RSC Advanced → Create-Sales hand-off (assembled per plan card below).
    goToCreateSalesFromPlan,
    // Lift the ACTIVE Advanced plan (the one chosen in the export chooser) up to the
    // parent so its shared Section 2 (PDF Forms / Quotation / ATAP / Create-Sales)
    // reflects THAT plan instead of Simple's active option. Fired as an "opt-like".
    onAdvActiveOpt,
}) {
    const { getBasePrice, getPrice, computePromoAmt, ccFromCash } = engine;

    // ── Customer Profile inputs (mirror the reference's grid). NOTE: phaseMode is
    //    the SHARED prop now, NOT a local state — so the engine prices identically. ──
    const [usageKwhStr, setUsageKwhStr] = useState('');
    const [billRmStr, setBillRmStr] = useState('');
    const [dayPctStr, setDayPctStr] = useState('30');
    const [panelLimitStr, setPanelLimitStr] = useState('');   // Max roof capacity (blank = no cap)
    const [salesName, setSalesName] = useState('');
    const [salesPhone, setSalesPhone] = useState('');

    // ── ONE GLOBAL editable add-on ROWS table (replaces the old Autocomplete).
    //    `globalAddonRows` is a single flat array of FULL rows (same shape as
    //    Simple's per-card mergedAddons rows) applied to ALL plans. Each row carries
    //    its own copy of the catalog fields + a per-row `_qty` (and an editable Base/
    //    Rate price). Each plan folds these rows in via computeAddonAmount/
    //    resolveAddonQty using THAT plan's panel count (so PerPanel add-ons differ
    //    per plan). The catalog also feeds the per-card Inverter stepper's upgrade
    //    chain (same source as RSC V2's getAddons). ──
    const [addons, setAddons] = useState([]);
    // `globalAddonRows` is the DEFAULT TEMPLATE (concrete+optimizer+hacking) used by
    // the ranking sweep. Each plan card then carries its OWN editable rows in
    // `addonRowsByKey` (key → rows), seeded from this template (user 2026-06-21) — so
    // a small plan and a big plan can carry different accessories.
    const [globalAddonRows, setGlobalAddonRows] = useState([]);
    const [addonRowsByKey, setAddonRowsByKey] = useState({});
    useEffect(() => {
        let cancelled = false;
        getAddons().then(r => { if (!cancelled) setAddons(r.data || []); }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // ── Row-management helpers — adapted from Simple's addAddonRowFromCatalog /
    //    updateAddonRow / removeAddonRow (residentialSolarCalculatorV2 ~L1561-1593),
    //    minus the origKey (ONE global array). The base+rate edit math (setPriceParts)
    //    in the row UI is copied VERBATIM from Simple. ──
    // The three plan-card keys (fixed). Per-card add-on rows are keyed by these.
    const PLAN_KEYS = ['fastest', 'highValue', 'max'];
    // Effective rows for a card: its own override, else the default template.
    const rowsForKey = (key) => (addonRowsByKey[key] !== undefined ? addonRowsByKey[key] : globalAddonRows);
    const curRowsForKey = (prev, key) => (prev[key] !== undefined ? prev[key] : globalAddonRows);
    // Per-card row helpers (key-scoped versions of Simple's addAddonRow/update/remove).
    const addAddonRow = (key, addonId) => {
        const cat = (addons || []).find(a => a.addonId === addonId);
        if (!cat) return;
        setAddonRowsByKey(prev => {
            const cur = curRowsForKey(prev, key);
            // Inverter-upgrade rows stay exclusive (one per card); others can repeat.
            if (INV_UP_RE.test(String(cat.itemName || '')) && cur.some(r => r.addonId === addonId)) return prev;
            const rowId = `r-${key}-${addonId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
            return { ...prev, [key]: [...cur, { ...cat, rowId, _qty: 1 }] };
        });
    };
    const updateAddonRow = (key, rowId, patch) => {
        setAddonRowsByKey(prev => ({ ...prev, [key]: curRowsForKey(prev, key).map(r => r.rowId === rowId ? { ...r, ...patch } : r) }));
    };
    const removeAddonRow = (key, rowId) => {
        setAddonRowsByKey(prev => ({ ...prev, [key]: curRowsForKey(prev, key).filter(r => r.rowId !== rowId) }));
    };

    // Seed the catalog's DEFAULT-SLOT (1–5) auto-include add-ons (user 2026-06-21) so
    // Advanced's price includes the SAME starter add-ons Simple folds into every option
    // card — making the cash / CC tiles TALLY with Simple. Mirrors Simple's seed
    // (residentialSolarCalculatorV2 ~L2710-2766): admin marks which add-ons are starter
    // rows via `defaultSlot`; they appear in every plan. PerPanel qty resolves per card.
    // NOTE: this is the admin's configured default-slot set — NOT the name-matched
    // Concrete/Optimizer/Hacking shortcuts (those are added on demand). If the admin
    // configured none, this seeds nothing and the cards stay empty. Re-seeds on phase
    // change (drops the old-phase set, resets per-card overrides to the new-phase set).
    useEffect(() => {
        if (!addons || addons.length === 0) return;
        const phaseLabel = phaseMode === '3phase' ? 'Three Phase' : 'Single Phase';
        const slotRows = addons
            .filter(a => a.defaultSlot != null && Number(a.defaultSlot) >= 1 && Number(a.defaultSlot) <= 5)
            .filter(a => addonMatchesPhase(a, phaseLabel) && !isCommercialAddon(a))
            .sort((a, b) => Number(a.defaultSlot) - Number(b.defaultSlot))
            .map(cat => ({ ...cat, rowId: `seed-${cat.addonId}-${cat.defaultSlot}`, _qty: cat.unitType === 'PerPanel' ? null : 1 }));
        setGlobalAddonRows(slotRows);
        setAddonRowsByKey({});   // every card picks up the (new-phase) default-slot set
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addons, phaseMode]);

    // Quick-add toggle (mirror Simple's shortcut buttons): add OR remove a shortcut
    // add-on ROW on THIS card. If a matching row is present, clicking removes it.
    const toggleShortcutAddon = (key, re) => {
        const phaseLabel = phaseMode === '3phase' ? 'Three Phase' : 'Single Phase';
        const cat = addons.find(x => !isCommercialAddon(x) && addonMatchesPhase(x, phaseLabel) && re.test(x.itemName || ''));
        if (!cat) return;
        const existing = rowsForKey(key).find(r => r.addonId === cat.addonId);
        if (existing) removeAddonRow(key, existing.rowId);
        else addAddonRow(key, cat.addonId);
    };

    // Per-plan expand + modal + snackbar
    const [expanded, setExpanded] = useState({});             // { fastest|highValue|max: bool }
    const [showCompare, setShowCompare] = useState(false);
    const [snack, setSnack] = useState('');

    // ── Plan selection (Change 1) — CHECKBOX MULTI-SELECT ────────────────
    // `selectedKeys` is an ORDERED array (selection order) so the export chooser
    // can map Opt A = 1st selected, Opt B = 2nd selected. Each card carries a
    // checkbox (+ a per-card toggle button) that adds/removes its key. The LIVE
    // draft of each card is lifted into `draftByKey` so the bottom Create-Sales bar
    // can build a sale from the FIRST selected plan.
    const [selectedKeys, setSelectedKeys] = useState([]);     // ordered keys
    const [draftByKey, setDraftByKey] = useState({});         // key → live buildPlanDraft(live)
    // LIVE display object per selected card (panels/kwp/battery/saving/cc/roi/inverter
    // AFTER stepper overrides) — `picks` only carries the PRE-stepper ranked values, so
    // the 图表 comparison table reads the stepper-adjusted live values from here.
    const [liveByKey, setLiveByKey] = useState({});           // key → card's live object
    // Per-card SAFETY-GATE viability (mirrors Simple's `viable`): a card whose LIVE
    // config breaches the safety ratio or exceeds the SEDA cap is NOT viable. Lifted
    // up so the prune effect can drop an unviable key from `selectedKeys` (an unviable
    // plan must never reach the export chooser / Create-Sales). Default-true so a card
    // that hasn't reported yet isn't spuriously dropped.
    const [viableByKey, setViableByKey] = useState({});       // key → bool
    const updatePlanViable = (key, ok) => setViableByKey(prev => (prev[key] === ok ? prev : { ...prev, [key]: ok }));
    const togglePlan = (key, draft, viable = true) => {
        // Guard: an unviable plan can never be ticked into `selectedKeys` (the card's
        // checkbox is also disabled, this is belt-and-braces). A toggle on an unviable
        // key only ever removes it.
        if (!viable) { setSelectedKeys(prev => prev.filter(k => k !== key)); return; }
        setSelectedKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
        setDraftByKey(prev => ({ ...prev, [key]: draft }));
    };
    // Keep the lifted draft fresh while a selected card's steppers change.
    const updatePlanDraft = (key, draft) => setDraftByKey(prev => (prev[key] === draft ? prev : { ...prev, [key]: draft }));
    // Keep the lifted LIVE display object fresh while a selected card's steppers change.
    const updatePlanLive = (key, live) => setLiveByKey(prev => (prev[key] === live ? prev : { ...prev, [key]: live }));

    // Per-card 10-Year Comparison focus (Change 3): when set, the modal opens
    // focused on just that plan's key.
    const [compareFocusKey, setCompareFocusKey] = useState(null);

    // ── Export chooser (Change 2) — mirrors Simple's "选择导出" bar ─────────
    // Opt A = 1st selected plan, Opt B = 2nd selected plan, A+B = both, 图表 =
    // comparison of selected plans. The picked WhatsApp message renders in the
    // preview box below (Copy / Open WhatsApp), exactly like Simple mode.
    const [activeExport, setActiveExport] = useState(null);   // 'a' | 'b' | 'ab' | 'chart' | null
    const [generatedMsg, setGeneratedMsg] = useState('');

    // ── Light Advanced Settings ──────────────────────────────────────────
    const [showAdv, setShowAdv] = useState(false);
    const [minBillCut, setMinBillCut] = useState('');         // global min bill-cut % floor
    const [battModes, setBattModes] = useState({ fastest: 'any', highValue: 'any', max: 'any' });

    // ── Advanced draft persistence (user 2026-06-21) — keep this view's OWN inputs
    //    across navigation/reload, like Simple's RSC draft. Restore once on mount, then
    //    save on change; a ref gates the save so the initial empty state doesn't clobber
    //    the restored draft. (Shared phase/promos persist via the parent's draft.) ──
    const advRestoredRef = useRef(false);
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem(ADV_DRAFT_KEY);
            if (raw) {
                const d = JSON.parse(raw);
                const S = (v, set) => { if (v !== undefined) set(v); };
                S(d.usageKwhStr, setUsageKwhStr); S(d.billRmStr, setBillRmStr); S(d.dayPctStr, setDayPctStr);
                S(d.panelLimitStr, setPanelLimitStr); S(d.minBillCut, setMinBillCut);
                S(d.battModes, setBattModes); S(d.selectedKeys, setSelectedKeys);
            }
        } catch { /* ignore corrupt draft */ }
        setTimeout(() => { advRestoredRef.current = true; }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (!advRestoredRef.current) return;
        try {
            sessionStorage.setItem(ADV_DRAFT_KEY, JSON.stringify({
                usageKwhStr, billRmStr, dayPctStr, panelLimitStr, minBillCut, battModes, selectedKeys,
            }));
        } catch { /* quota/serialize errors are non-fatal */ }
    }, [usageKwhStr, billRmStr, dayPctStr, panelLimitStr, minBillCut, battModes, selectedKeys]);

    // Mutually-exclusive kWh ↔ RM (same handlers as the other views).
    const onUsageKwhChange = (v) => {
        setUsageKwhStr(v);
        const n = parseFloat(v) || 0;
        setBillRmStr(n > 0 ? String(Math.round(kwhToBill(n))) : '');
    };
    const onBillRmChange = (v) => {
        setBillRmStr(v);
        const n = parseFloat(v) || 0;
        setUsageKwhStr(n > 0 ? String(Math.round(billToKwh(n))) : '');
    };

    const usageKwh = parseFloat(usageKwhStr) || 0;
    const bill = usageKwh > 0 ? Math.round(calcC2Bill(usageKwh, 0) * 100) / 100 : 0;
    const dayPctNum = parseFloat(dayPctStr) || 30;
    const dayPct = dayPctNum / 100;
    const panelLimit = parseInt(panelLimitStr, 10) > 0 ? parseInt(panelLimitStr, 10) : 999;
    const minCutPct = parseFloat(minBillCut) > 0 ? parseFloat(minBillCut) : 0;
    const is3p = phaseMode === '3phase';
    const phaseLabel = is3p ? 'Three Phase' : 'Single Phase';

    // Catalog add-on RM folded into every priced config — mirroring Simple's tiles,
    // which add the selected add-ons' RM to the FINAL cash AFTER the promo discount.
    // GLOBAL picker → one set of add-ons for ALL plans, but the AMOUNT is computed
    // PER PLAN using that plan's panel count: PerPanel add-ons multiply by the plan's
    // panels (resolveAddonQty), so `addOnRmFor(panels)` may differ per plan. Computed
    // EXACTLY like Simple (computeAddonAmount(a, resolveAddonQty(a, panels)).total).
    // Memoised by the selected rows so each card's pricing closes over a stable fn.
    const addOnRmFor = useMemo(() => (panels) =>
        globalAddonRows.reduce((s, r) => {
            const q = resolveAddonQty(r, panels);
            return q > 0 ? s + computeAddonAmount(r, q).total : s;
        }, 0),
        [globalAddonRows]);

    // pricingRows for generateCandidates — atap rows mapped + roof-limited (single-phase
    // honours the panel cap; 3-phase keeps all rows, exactly like Simple's billHyd).
    const pricingRows = useMemo(() => {
        const rows = atapPrices.map(atapToPricingRow);
        return is3p ? rows : rows.filter(r => !r.panelQty || r.panelQty <= panelLimit);
    }, [atapPrices, is3p, panelLimit]);

    const billBatteryOptions = batteryOptions;

    // 16kWh battery option (the only stepper unit — matches RSC V2's per-card
    // battery stepping). Falls back to the first option if no 16kWh row exists.
    const batt16 = useMemo(
        () => batteryOptions.find(b => Number(b.kwhEach) === 16) || batteryOptions[0] || null,
        [batteryOptions]
    );

    // Sorted atap rows for the Panels stepper (ascending by panel count, only rows
    // priced for the active phase). Each row → panel count → kWp → inverter → price.
    const sortedRows = useMemo(() => {
        const rows = atapPrices
            .filter(r => (is3p ? (r.cash3p > 0) : (r.cash > 0)) && r.panels > 0)
            .filter(r => is3p || r.panels <= panelLimit)
            .slice()
            .sort((a, b) => a.panels - b.panels);
        return rows;
    }, [atapPrices, is3p, panelLimit]);

    // (The inverter-upgrade step map is now built PER CARD — see PlanCard's
    //  invStepByFromCard — since the stepper drives the card's own add-on rows.)

    const invLabelOf = useMemo(() => (kwp) => {
        const r = atapPrices.find(p => Math.abs(p.kwp - kwp) < 0.01);
        if (!r) return '';
        const s = is3p ? (r.inverter3p || r.inverter) : r.inverter;
        return s ? String(s) : '';
    }, [atapPrices, is3p]);

    // ── Price one priced-pkg EXACTLY like Simple's Bill-tab option card. Returns
    //    the full price shape (quote = pre-rebate cash, investment = after-rebate,
    //    CC tiles converted off the pre-rebate quote). The engine functions close
    //    over the SHARED phase + promo flags, so these numbers equal Simple's. ──
    const priceLikeSimple = (pkg, monthlySaving, addonRows) => {
        // disc = promo only (Advanced has no global Bill-tab manual discount).
        const disc = { promoAmt: computePromoAmt(pkg), discAmt: 0, discPct: 0 };
        // Catalog add-on RM for THIS plan, from the supplied rows (per-card) — or the
        // default template when omitted (the ranking sweep). PerPanel add-ons use the
        // plan's own panel count (resolveAddonQty), so the fold can differ per plan.
        const panels = Number(pkg.panels) || 0;
        const rowsToUse = addonRows || globalAddonRows;
        const addOnRm = (rowsToUse || []).reduce((s, r) => { const q = resolveAddonQty(r, panels); return q > 0 ? s + computeAddonAmount(r, q).total : s; }, 0);
        // getPrice already subtracts ALL promos (incl. the SuRIA gov rebate, which is
        // inside computePromoAmt). Add-ons are folded AFTER, exactly like Simple.
        const investCash = getPrice(pkg, 'cash', disc) + addOnRm;     // after-rebate net investment
        const govRebate = promoGovRebate ? calcGovRebate(invKwOf(pkg)) : 0;
        const preRebateCash = investCash + govRebate;                  // the QUOTE shown to the customer
        // CC tiles convert the PRE-rebate quote (SuRIA claimed from TNB after install).
        const cc = {};
        for (const term of CC_TERMS) cc[term] = ccFromCash(preRebateCash, term);
        const yearlySaving = Math.round(monthlySaving * 12);
        // ROI on the quote (normal) and on the net investment (after the SuRIA rebate).
        const roiYears = preRebateCash > 0 && yearlySaving > 0
            ? Math.round(preRebateCash / yearlySaving * 100) / 100 : Infinity;
        const investRoi = investCash > 0 && yearlySaving > 0
            ? Math.round(investCash / yearlySaving * 100) / 100 : roiYears;
        return {
            // netCash = the pre-rebate QUOTE — drives the headline Cash, CC tiles,
            // WhatsApp + 10-year modal (all mirror Simple's headline + CC base).
            netCash: preRebateCash, preRebateCash, investCash, govRebate, cc,
            monthlySaving, yearlySaving, roiYears, investRoi,
        };
    };

    // ── Single source of truth: hydrate ONE config (atap row + battery qty + the
    // inverter-upgrade ROWS present in addonRows) into the full priced/saving shape.
    // Both the ranking sweep and each interactive PlanCard call this, so a stepper
    // change recomputes EXACTLY the same way the ranked plan was built. The inverter
    // upgrade is now a REGULAR add-on ROW (user 2026-06-21) — its RM flows through
    // addOnRm like every other add-on, and effInvKw is DERIVED by walking the
    // inverter-upgrade rows from the natural inverter (mirrors Simple's walkInvChain).
    // The 3rd param is ignored (kept for call-site compatibility). Returns null when
    // the config can't be priced.
    const hydrateConfig = useMemo(() => (row, battQty, _ignoredInvStep = 0, addonRows) => {
        if (!row || row.panels <= 0) return null;
        const battOpt = battQty > 0 && batt16
            ? batt16
            : { type: 'none', kwhEach: 0, cashEach: 0, ccEach: 0 };
        const pr = atapToPricingRow(row);
        // Build the base candidate via OUR engine's generator (single row).
        const cands = generateCandidates([pr], usageKwh, bill, battOpt, battQty, phaseMode, dayPct);
        let c = cands[0];
        if (!c) {
            // generateCandidates drops rows that breach the safety ratio / SEDA cap /
            // battery sizing. For an explicit user step we still want a card, so build
            // the candidate fields directly (same math, sans the auto-filters).
            const cashSingle = pr.cashPrice, cc36Single = pr.cc36Price;
            const cash3p = pr.cash3p8k, cc36_3p = pr.cc36_3p8k;
            const basePrice = is3p ? cash3p : cashSingle;
            if (!basePrice) return null;
            const battKwhTotal = Math.round((battOpt.kwhEach || 0) * battQty * 10) / 10;
            const savingMo = calcSaving(row.kwp, usageKwh, battKwhTotal, bill, dayPct);
            c = {
                kwp: row.kwp, panels: row.panels, panelWatt: undefined,
                battType: battOpt.type, battQty, battKwhEach: battOpt.kwhEach || 0, battKwhTotal,
                battUnitPrice: battOpt.cashEach || 0,
                totalPrice: basePrice + (battOpt.cashEach || 0) * battQty,
                ccPrice: (is3p ? cc36_3p : cc36Single) + (battOpt.ccEach || 0) * battQty,
                totalPrice3p: cash3p > 0 ? cash3p + (battOpt.cashEach || 0) * battQty : 0,
                ccPrice3p: cc36_3p > 0 ? cc36_3p + (battOpt.ccEach || 0) * battQty : 0,
                savingMo,
            };
        }

        // Derive the EFFECTIVE inverter kW from the inverter-upgrade ROWS present in
        // addonRows (mirrors Simple's walkInvChain / effInvKw): walk the same-phase
        // upgrade rows from the natural inverter. The upgrade RM is NOT folded into the
        // system price anymore — it flows through addOnRm in priceLikeSimple like every
        // other add-on (the upgrade row is in addonRows). invCost is reported (from the
        // rows) only for the "incl. add-ons" display + the create-sales line naming.
        const rowsToUse = addonRows || globalAddonRows;
        const naturalKw = parseInverterKw(invLabelOf(row.kwp));
        const phaseDigit = is3p ? '3' : '1';
        let curKw = naturalKw, invCost = 0;
        if (naturalKw && (rowsToUse || []).length) {
            for (let i = 0; i < 12; i++) {
                const step = (rowsToUse || []).find(a => {
                    const m = INV_UP_RE.exec(String(a.itemName || ''));
                    return m && m[1] === phaseDigit && Math.abs(parseFloat(m[2]) - curKw) < 0.01;
                });
                if (!step) break;
                const m = INV_UP_RE.exec(String(step.itemName || ''));
                curKw = parseFloat(m[4]);
                invCost += addonAmount(step);
            }
        }
        const upgraded = curKw && curKw !== naturalKw;

        // The catalog add-ons (INCLUDING the inverter-upgrade row) are folded in
        // priceLikeSimple via addOnRm — NOT into the system base price. We only set
        // _addonInvKw so the SHARED engine's invKwOf/computePromoAmt/buildBreakdown
        // read the UPGRADED inverter kW (keeps the SuRIA rebate on the right kW).
        const priced = {
            ...c,
            // Honour _addonInvKw so the SHARED engine's invKwOf/computePromoAmt/
            // buildBreakdown all read the UPGRADED inverter kW (mirrors RSC V2's
            // synthesized upgrade pkg) — keeps the SuRIA rebate on the right kW.
            ...(upgraded ? { _addonInvKw: curKw } : {}),
        };

        const monthlySaving = c.savingMo;
        const price = priceLikeSimple(priced, monthlySaving, rowsToUse);
        const savingsPct = bill > 0 ? Math.round(monthlySaving / bill * 1000) / 10 : 0;
        const flow = energyFlow(c.kwp, usageKwh, c.battKwhTotal, dayPct);
        return {
            pkg: priced,
            kwp: c.kwp, panels: c.panels, panelWatt: c.panelWatt,
            battType: c.battType, battQty: c.battQty, battKwhEach: c.battKwhEach, battKwhTotal: c.battKwhTotal,
            inverterLabel: invLabelOf(c.kwp),
            naturalInvKw: naturalKw, effInvKw: curKw || naturalKw,
            ...price,
            roiYears: Number.isFinite(price.roiYears) ? price.roiYears : Infinity,
            savingsPct, yieldRate: c.yieldRate,
            // THIS card's rows → its add-on RM (PerPanel uses c.panels), plus the
            // editable rows so buildPlanDraft can freeze identical line items per card.
            addOnRm: (rowsToUse || []).reduce((s, r) => { const q = resolveAddonQty(r, Number(c.panels) || 0); return q > 0 ? s + computeAddonAmount(r, q).total : s; }, 0),
            addOnRows: rowsToUse, invCost, flow,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usageKwh, bill, dayPct, phaseMode, batt16, is3p, getPrice, computePromoAmt, ccFromCash,
        promoBackupbox, promoFirst640, promoPerBatt, promoGovRebate, invKwOf, calcGovRebate,
        addOnRmFor, globalAddonRows, invLabelOf]);

    // ── Build a Create-Sales `draft` from a hydrated plan (`live`) ───────────
    // Replicates Simple's "Sale-mapping breakdown" (residentialSolarCalculatorV2's
    // buildResidentialFormPayload ~L2036-2060 + buildSaleLineItems ~L2132) using the
    // SAME formulas — the engine + promo flags are shared, so the numbers match a
    // Simple hand-off exactly. The parent's goToCreateSalesFromPlan turns this draft
    // into the IDENTICAL rscPrefill shape goToCreateSalesForm uses.
    //   - oriCashPrice = system cash (panel-only), battery removed. The inverter-upgrade
    //     RM is NOT in the system price anymore — it's a regular add-on ROW, so it flows
    //     through addOnPrice. This equals Simple's baseCashRaw − battCashTotal.
    //   - asternDiscount = Backupbox Removal + FIRST640 (same phase-aware amounts).
    //   - perBattDiscount = RM 800 × battQty for 16kWh batteries (folded into battery net).
    //   - agentDiscount = 0 (Advanced has no per-card manual discount, like Simple's
    //     rowDisc when unset).
    //   - addOnPrice = battery cash + selected catalog add-ons (incl. inverter upgrade).
    //   - lineItems = battery (NET, per-batt discount folded) + each selected add-on
    //     (the inverter-upgrade row included).
    const buildPlanDraft = useCallback((live) => {
        if (!live || !live.pkg) return null;
        const pkg = live.pkg;
        const battQty = Number(pkg.battQty) || 0;
        const battKwhEach = Number(pkg.battKwhEach) || 0;
        const battUnit = Number(pkg.battUnitPrice) || 0;
        const battCashTotal = battUnit * battQty;
        const baseCashRaw = getBasePrice(pkg, 'cash');      // panel + battery (inverter
        //   upgrade is NOT folded into the system price anymore — it's a real add-on
        //   ROW in addonRows, so its RM is already in addOnPrice via pdfAddonItems).
        // oriCashPrice = panel-only system (base − battery). The inverter-upgrade cost
        // lives in the add-on lines now (user 2026-06-21), so there's nothing to back
        // out here — commission stays on the system only, matching Simple's hand-off.
        const oriCashPrice = Math.max(0, baseCashRaw - battCashTotal);   // panel-only system

        // Astern discount = Backupbox Removal + FIRST640 only (same as Simple).
        const boxRemoved = promoBackupbox && battQty === 0;
        const asternDiscount =
            (boxRemoved ? (is3p ? 2500 : 1600) : 0) +
            (promoFirst640 ? (boxRemoved ? (is3p ? 1600 : 800) : (is3p ? 3000 : 1800)) : 0);
        const perBattDiscount = (promoPerBatt && battKwhEach === 16) ? 800 * battQty : 0;
        const agentDiscount = 0;

        // ── Selected catalog add-ons → PDF line items, EXACTLY like Simple's
        //    pdfAddonItems (residentialSolarCalculatorV2 ~L1958) + buildSaleLineItems.
        //    PerPanel add-ons resolve qty = THIS plan's panels; base+rate collapses to
        //    qty=1 at the total (or qty=panels × unitPrice when there's a per-panel rate
        //    over multiple panels) so Qty × UnitPrice always equals the total. The
        //    inverter-upgrade row (driven by the stepper) is a regular catalog row now,
        //    so it maps through here like any other add-on (no special-casing). ──
        const planPanels = Number(live.panels) || 0;
        // The inverter-upgrade row is a REGULAR add-on row now (user 2026-06-21) — keep
        // it in the catalog list so it flows into pdfAddonItems / addOnPrice / lineItems
        // like every other add-on (no synthesized line, no system-price fold).
        const catalogAddons = (live.addOnRows || []);
        const pdfAddonItems = catalogAddons.map(a => {
            const unitQty = resolveAddonQty(a, planPanels);
            const r = computeAddonAmount(a, unitQty);
            let qty = 1, unitPrice = 0;
            if (r.base > 0 && unitQty > 1) {
                qty = unitQty; unitPrice = r.total / unitQty;
            } else if (r.base > 0) {
                qty = 1; unitPrice = r.total;
            } else if (r.perUnit > 0) {
                qty = unitQty; unitPrice = r.perUnit;
            } else {
                qty = 1; unitPrice = r.total;
            }
            return { id: String(a.addonId || ''), name: a.itemName || 'Add-on', qty, price: unitPrice };
        });
        const addonTotal = pdfAddonItems.reduce((s, x) => s + x.qty * x.price, 0);
        // Add-on cash pile = battery + selected catalog add-ons (non-upgrade).
        const addOnPrice = battCashTotal + addonTotal;

        // Line items (PascalCase, mirrors buildSaleLineItems). Battery shown NET with
        // the per-battery promo folded into its unit price.
        const lineItems = [];
        if (battQty > 0) {
            const grossTotal = battCashTotal;
            const netTotal = Math.max(0, grossTotal - perBattDiscount);
            const unitNet = battQty ? Math.round(netTotal / battQty) : netTotal;
            lineItems.push({
                ItemName: `Battery ${battKwhEach}kWh`,
                Category: 'Battery',
                IsBattery: true,
                Qty: battQty,
                BatteryKwh: battKwhEach * battQty,
                UnitPrice: unitNet,
                LineTotal: netTotal,
                SortOrder: 0,
            });
        }
        pdfAddonItems.forEach((a) => {
            // Backup box removed by promo is not a sale line item (matches Simple).
            if (boxRemoved && /backup/i.test(a.name || '')) return;
            lineItems.push({
                ItemName: a.name,
                Category: null, IsBattery: false,
                Qty: Number(a.qty) || 1, BatteryKwh: null,
                UnitPrice: Number(a.price) || 0,
                LineTotal: (Number(a.price) || 0) * (Number(a.qty) || 1),
                SortOrder: lineItems.length,
            });
        });

        return {
            panelQty: Number(live.panels) || 0,
            energy: Number(live.kwp) || 0,
            panelWatt: String(Number(live.panelWatt) || 640),
            paymentMethod: 'CASH',
            originalPrice: oriCashPrice,
            asternDiscount, perBattDiscount, agentDiscount,
            addOnPrice,
            batteryKwh: battKwhEach,   // per-unit kWh (for the camelCase line item batteryKwh)
            lineItems,
            promoBackupbox, promoFirst640, promoPerBatt,
        };
    }, [getBasePrice, is3p, promoBackupbox, promoFirst640, promoPerBatt]);

    // ── Build + hydrate candidates, then rank ────────────────────────────
    const hydrated = useMemo(() => {
        if (!usageKwh || !pricingRows.length) return [];
        const battAnchor = billBatteryOptions[0] || { type: 'none', kwhEach: 0, cashEach: 0, ccEach: 0 };
        const cands = [];
        // No-battery anchor (exactly Simple's billHyd no-batt call).
        const noBatt = generateCandidates(pricingRows, usageKwh, bill, { type: battAnchor.type, kwhEach: 0, cashEach: 0, ccEach: 0 }, 0, phaseMode, dayPct);
        cands.push(...noBatt.map(c => ({ ...c, battType: battAnchor.type, battQty: 0, battKwhEach: 0, battKwhTotal: 0 })));
        // With each battery option (large ≥10kWh → 1,2,3; small → 1,2 — same as billHyd).
        for (const batt of billBatteryOptions) {
            const qtys = batt.kwhEach >= 10 ? [1, 2, 3] : [1, 2];
            for (const qty of qtys) cands.push(...generateCandidates(pricingRows, usageKwh, bill, batt, qty, phaseMode, dayPct));
        }

        const labelOf = (kwp) => {
            const r = atapPrices.find(p => Math.abs(p.kwp - kwp) < 0.01);
            if (!r) return '';
            const s = is3p ? (r.inverter3p || r.inverter) : r.inverter;
            return s ? String(s) : '';
        };

        return cands.map(c => {
            // The candidate IS the pkg. Price it the SAME way Simple's option card does:
            // getPrice (after all promos incl. SuRIA) + add-ons → after-rebate; + rebate
            // back → pre-rebate QUOTE; CC off the quote. (No inverter upgrade at ranking
            // time — steppers add it interactively per card.)
            const monthlySaving = c.savingMo; // calcSaving(kwp, usageKwh, battTotal, bill, dayPct)
            const price = priceLikeSimple(c, monthlySaving);
            const savingsPct = bill > 0 ? Math.round(monthlySaving / bill * 1000) / 10 : 0;
            const flow = energyFlow(c.kwp, usageKwh, c.battKwhTotal, dayPct);
            const natKw = parseInverterKw(labelOf(c.kwp));
            return {
                pkg: c,
                kwp: c.kwp, panels: c.panels, panelWatt: c.panelWatt,
                battType: c.battType, battQty: c.battQty, battKwhEach: c.battKwhEach, battKwhTotal: c.battKwhTotal,
                inverterLabel: labelOf(c.kwp),
                naturalInvKw: natKw, effInvKw: natKw,
                ...price,
                roiYears: Number.isFinite(price.roiYears) ? price.roiYears : Infinity,
                savingsPct, yieldRate: c.yieldRate,
                addOnRm: addOnRmFor(Number(c.panels) || 0), addOnRows: globalAddonRows, invCost: 0, flow,
            };
        }).filter(c => c.netCash > 0 && c.monthlySaving > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usageKwh, bill, dayPct, phaseMode, pricingRows, billBatteryOptions, atapPrices, is3p,
        getPrice, computePromoAmt, ccFromCash,
        promoBackupbox, promoFirst640, promoPerBatt, promoGovRebate, invKwOf, calcGovRebate,
        addOnRmFor, globalAddonRows]);

    // Per-card battery filter helper (light Advanced Settings).
    const filterByBatt = (pool, mode) => {
        if (mode === 'with') return pool.filter(c => c.battQty > 0);
        if (mode === 'without') return pool.filter(c => c.battQty === 0);
        return pool;
    };

    const PICK_META = {
        fastest:   { cn: '⚡ 最快回本', en: 'Fastest ROI',  my: 'ROI Terpantas', accent: '#0EA5E9', bg: 'linear-gradient(135deg,#0a4a9e,#0f62cc)', bd: 'rgba(255,255,255,0.15)' },
        highValue: { cn: '💎 高性价比', en: 'High-value',   my: 'Nilai Tinggi',  accent: '#1A8CFF', bg: 'linear-gradient(135deg,#1e1b4b,#312e81)', bd: 'rgba(255,255,255,0.15)' },
        max:       { cn: '🔋 最大省电', en: 'Max Savings',  my: 'Jimat Maksima', accent: '#16A34A', bg: 'linear-gradient(135deg,#064e3b,#065f46)', bd: 'rgba(255,255,255,0.15)' },
    };

    // Rank each card from its own (battery-mode-filtered + min-cut-floored) pool so
    // the light per-card settings actually steer the pick. With everything 'any' and
    // no floor this reduces to a single rankPlans over the full pool.
    const picks = useMemo(() => {
        if (!hydrated.length) return [];
        const floored = minCutPct > 0 ? hydrated.filter(c => c.savingsPct >= minCutPct) : hydrated;
        return ['fastest', 'highValue', 'max'].map(key => {
            const pool = filterByBatt(floored, battModes[key]);
            const ranked = rankPlans(pool);
            const plan = ranked[key];
            if (!plan) return null;
            const meta = PICK_META[key];
            return { ...plan, key, label: { cn: meta.cn, en: meta.en, my: meta.my }, meta };
        }).filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hydrated, minCutPct, battModes]);

    const hasResults = picks.length > 0;

    // Drop stale selections when the ranked picks no longer contain them (inputs /
    // advanced settings changed) OR when a still-present plan's LIVE config has gone
    // unviable (safety-ratio breach / over SEDA). Keeps the Create-Sales + export
    // chooser honest — an unviable plan must never be handed downstream.
    useEffect(() => {
        setSelectedKeys(prev => {
            const next = prev.filter(k => picks.some(p => p.key === k) && viableByKey[k] !== false);
            return next.length === prev.length ? prev : next;
        });
    }, [picks, viableByKey]);

    // Auto-select ALL options by default (user 2026-06-21) — seed selectedKeys with
    // every pick once the ranking is available, IF nothing is already selected (so a
    // restored draft wins). The prune effect above drops any key that turns out
    // unviable. Seeds once, so the user can freely deselect afterwards.
    const didAutoSelectRef = useRef(false);
    useEffect(() => {
        if (didAutoSelectRef.current || !picks.length) return;
        didAutoSelectRef.current = true;
        setSelectedKeys(prev => (prev.length === 0 ? picks.map(p => p.key) : prev));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [picks]);

    // Fixed letter per plan by CARD POSITION (A/B/C) — NOT selection order. Selecting
    // cards 2+3 means B+C (per user 2026-06-21), regardless of which was ticked first.
    const letterForKey = (key) => String.fromCharCode(65 + picks.findIndex(p => p.key === key));
    // Selected plans in CARD order, so the export buttons + combo + table follow the
    // fixed letters (e.g. B then C), not the order they were ticked.
    const selectedInCardOrder = useMemo(
        () => picks.filter(p => selectedKeys.includes(p.key)),
        [picks, selectedKeys]
    );

    // The selected plans as their LIVE (stepper-adjusted) shape for the 图表 comparison
    // table, in card order. Prefer the lifted liveByKey[key]; fall back to the pick.
    const comparisonPlans = useMemo(
        () => selectedInCardOrder.map(p => liveByKey[p.key] || p),
        [selectedInCardOrder, liveByKey]
    );
    // For the inline 10-year body: keep the pick's meta/label/key but overlay the LIVE
    // stepped values (monthlySaving, netCash, kwp, battery) so it reflects the steppers.
    const tenYearPlans = useMemo(
        () => selectedInCardOrder.map(p => ({ ...p, ...(liveByKey[p.key] || {}) })),
        [selectedInCardOrder, liveByKey]
    );
    // Fixed letters for the table columns (方案 B / 方案 C …), in card order.
    const comparisonLetters = useMemo(
        () => selectedInCardOrder.map(p => letterForKey(p.key)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedInCardOrder, picks]
    );

    // ── Lift the ACTIVE Advanced plan up so Section 2 (shared PDF Forms / Quotation /
    //    ATAP / Create-Sales) uses THIS plan instead of Simple's active option.
    //    Active plan key = whichever selected plan matches activeExport (handles a
    //    'custom_<key>' choice too); else the FIRST selected plan (card order). We then
    //    package its LIVE (stepper-adjusted) pkg + add-on rows into an "opt-like" object
    //    shaped like Simple's seqOptions entry, so buildResidentialFormPayload can price
    //    it through the SAME getBasePrice/getPrice/promos path (identical numbers to the
    //    Advanced card). The LIVE pkg comes from liveByKey[key].pkg (set by each card);
    //    its rows come from rowsForKey(key). ──
    const activeAdvKey = useMemo(() => {
        if (selectedInCardOrder.some(p => p.key === activeExport)) return activeExport;
        if (typeof activeExport === 'string' && activeExport.startsWith('custom_')
            && selectedInCardOrder.some(p => p.key === activeExport.replace('custom_', ''))) {
            return activeExport.replace('custom_', '');
        }
        return selectedInCardOrder[0]?.key || null;
    }, [activeExport, selectedInCardOrder]);

    // SIGNATURE GUARD (critical): `picks` recomputes on every parent re-render (the
    // engine fns it closes over are fresh each render), which makes this effect re-fire.
    // If we called onAdvActiveOpt (→ parent setState) on every fire, parent re-render →
    // picks recompute → fire → ... → "Maximum update depth exceeded". So only push up
    // when the active plan's MEANINGFUL content actually changes (key/price/inverter/
    // bill/add-ons), tracked via a ref. (loop reproduced + fixed 2026-06-22.)
    const advSigRef = useRef(null);
    useEffect(() => {
        if (!onAdvActiveOpt) return;
        const fire = (opt, sig) => {
            if (advSigRef.current === sig) return;   // unchanged → skip the parent setState
            advSigRef.current = sig;
            onAdvActiveOpt(opt);
        };
        if (!activeAdvKey) { fire(null, 'null'); return; }
        const live = liveByKey[activeAdvKey];
        // live carries the stepper-adjusted pkg + add-on rows. Fall back to the ranked
        // pick's pkg/rows if a card hasn't reported its live object yet.
        const pick = picks.find(p => p.key === activeAdvKey);
        const pkg = (live && live.pkg) || (pick && pick.pkg) || null;
        if (!pkg) { fire(null, 'null'); return; }
        const addonRows = (live && live.addOnRows) || rowsForKey(activeAdvKey) || [];
        const sig = JSON.stringify({
            k: activeAdvKey, kwp: pkg.kwp, p: pkg.panels, tp: pkg.totalPrice,
            cc: pkg.ccPrice, inv: pkg._addonInvKw, u: usageKwhStr, d: dayPctStr,
            rows: (addonRows || []).map(r => [r.addonId, r.price, r._qty, r.name]),
        });
        fire({
            key: activeAdvKey,
            letter: letterForKey(activeAdvKey),
            meta: PICK_META[activeAdvKey],
            pkg,
            addonRows,
            panels: Number((live && live.panels) ?? (pick && pick.panels)) || 0,
            kwp: Number((live && live.kwp) ?? (pick && pick.kwp)) || 0,
            // Advanced bill inputs — the parent syncs its tnbUsageKwh/billRm/dayPct from
            // these so its `bill` (which gates Create Sales + feeds the form) matches the
            // Advanced panel. Without this the parent's bill is empty → Create Sales is
            // disabled in Advanced mode (user 2026-06-21).
            usageStr: usageKwhStr, billStr: billRmStr, dayPctStr,
        }, sig);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeAdvKey, liveByKey, picks, addonRowsByKey, globalAddonRows, onAdvActiveOpt, usageKwhStr, billRmStr, dayPctStr]);

    // Build the WhatsApp message for an export choice via the SHARED builder.
    //   <plan.key> → that single plan;  'combo' → ALL selected (card order);
    //   'chart' → no message (the inline summary table renders instead).
    const buildExportMsg = (which) => {
        if (which === 'chart' || which === 'tenyear') return '';
        const plans = which === 'combo'
            ? selectedInCardOrder
            : selectedInCardOrder.filter(p => p.key === which);
        return buildRoiSearcherMsg({
            plans, bill, usageKwh, dayPct: dayPctNum, phaseMode, customerName: salesName, lang, ccFromCash,
        });
    };

    // ── Customizable WhatsApp (user 2026-06-21) — same editable template as Simple
    //    mode (shared localStorage). "Custom A/B/A+B" fills the template with THIS
    //    plan's LIVE numbers. Choosing custom vs preset only changes the message text. ──
    const [customWaTemplate, setCustomWaTemplate] = useState(() => loadWaTemplate());
    const [showWaTemplate, setShowWaTemplate] = useState(false);
    const saveWaTemplate = (val) => { setCustomWaTemplate(val); saveWaTemplateStr(val); };
    // Placeholder vars for one LIVE plan (stepper-adjusted), mirroring Simple's set.
    const planVars = (p) => {
        if (!p) return {};
        const phaseDigit = is3p ? '3' : '1';
        const battery = Number(p.battQty) > 0 ? `${p.battQty}x ${p.battKwhEach}kWh GoodWe Battery` : t('无电池', 'No Battery', 'Tiada Bateri');
        const cc36 = (p.cc && p.cc[36] != null) ? p.cc[36] : 0;
        return {
            name: (salesName || '').toUpperCase(), optLetter: letterForKey(p.key),
            phase: is3p ? t('3Phase', '3 Phase', '3 Fasa') : t('单相', 'Single Phase', 'Fasa Tunggal'),
            kwp: p.kwp, panels: p.panels, panelWatt: p.panelWatt, battery,
            inverter: `${phaseDigit}p${p.effInvKw}k`,
            bill: bill > 0 ? bill.toFixed(2) : '', usage: usageKwh ? Math.round(usageKwh) : '', dayPct: dayPctNum,
            saving: Math.round(Number(p.monthlySaving) || 0).toLocaleString(), savingPct: p.savingsPct,
            roi: Number.isFinite(p.roiYears) ? p.roiYears : '', yield: p.yieldRate,
            cash: Math.round(Number(p.preRebateCash || p.netCash) || 0).toLocaleString(),
            netCash: Math.round(Number(p.investCash || p.netCash) || 0).toLocaleString(), cashOri: '',
            cc36: Math.round(Number(cc36) || 0).toLocaleString(), netCc: '',
            rebate: Math.round(Number(p.govRebate) || 0).toLocaleString(), promoBlock: '',
        };
    };
    // Build a custom message for a plan key (or 'custom_combo' = all selected).
    const buildCustomMsg = (which) => {
        const tpl = customWaTemplate || DEFAULT_WA_TEMPLATE;
        const keys = which === 'custom_combo' ? selectedInCardOrder.map(p => p.key) : [String(which).replace('custom_', '')];
        const plans = keys.map(k => {
            const pick = selectedInCardOrder.find(p => p.key === k);
            return pick ? { ...pick, ...(liveByKey[k] || {}) } : null;
        }).filter(Boolean);
        if (!plans.length) return '';
        return plans.map(p => fillWaTemplate(tpl, planVars(p))).join('\n\n———\n\n');
    };
    const onPickCustom = (which) => { setActiveExport(which); setGeneratedMsg(buildCustomMsg(which) || ''); };

    // Export chooser click → generate + show the message in the preview box.
    // 图表 (chart) is NOT a WhatsApp export — it toggles the inline comparison table,
    // so no message is generated for it.
    const onPickExport = (which) => {
        if (which === 'chart') {
            setActiveExport(prev => (prev === 'chart' ? null : 'chart'));
            setGeneratedMsg('');
            return;
        }
        const msg = buildExportMsg(which);
        setActiveExport(which);
        setGeneratedMsg(msg || '');
    };

    // Keep the preview message in sync while inputs / selections / steppers change.
    // Skip 'chart' — it has no WhatsApp message.
    useEffect(() => {
        if (!activeExport || activeExport === 'chart' || activeExport === 'tenyear') return;
        const msg = String(activeExport).startsWith('custom_') ? buildCustomMsg(activeExport) : buildExportMsg(activeExport);
        setGeneratedMsg(msg || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeExport, selectedKeys, picks, bill, usageKwh, dayPctNum, phaseMode, salesName, lang, liveByKey, customWaTemplate]);

    // ── Copy / WhatsApp open (mirror Simple's preview-box actions) ────────
    const copyMsg = async (text) => {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            setSnack(t('已复制 WhatsApp 文案', 'WhatsApp message copied', 'Mesej WhatsApp disalin'));
        } catch {
            setSnack(t('复制失败', 'Copy failed', 'Gagal menyalin'));
        }
    };
    const openWAText = (text) => {
        if (!text) return;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    };

    // ── 图表 comparison table: download PNG (desktop) / native share (mobile) ──
    // Mirrors Simple's downloadComparison: html2canvas the rendered table, then on
    // mobile open the share sheet, else download.
    const chartRef = useRef(null);
    const chartFileName = () => `Comparison - ${(salesName || 'Astern').trim()}.png`;
    const captureChart = async () => {
        if (!chartRef.current) return null;
        try { return await html2canvas(chartRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true }); }
        catch { setSnack(t('生成图片失败', 'Image generation failed', 'Gagal menjana imej')); return null; }
    };
    const downloadChart = async () => {
        const canvas = await captureChart(); if (!canvas) return;
        const a = document.createElement('a'); a.download = chartFileName(); a.href = canvas.toDataURL('image/png'); a.click();
    };
    const shareChart = async () => {
        const canvas = await captureChart(); if (!canvas) return;
        const fileName = chartFileName();
        const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
        const file = blob ? new File([blob], fileName, { type: 'image/png' }) : null;
        if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
            try { await navigator.share({ files: [file], title: fileName }); }
            catch (err) { if (err && err.name !== 'AbortError') setSnack(t('分享失败', 'Share failed', 'Gagal kongsi')); }
        } else {
            setSnack(t('此设备不支持分享，请用下载', 'Sharing not supported here — use Download', 'Kongsi tidak disokong — guna Muat Turun'));
        }
    };
    // View-level "Copy WhatsApp" — copies whatever is selected (or all picks if none).
    const handleCopyWa = () => {
        const plans = selectedInCardOrder.length ? selectedInCardOrder : picks;
        const msg = buildRoiSearcherMsg({
            plans, bill, usageKwh, dayPct: dayPctNum, phaseMode, customerName: salesName, lang, ccFromCash,
        });
        copyMsg(msg);
    };

    // ── Bill scenario table (re-runs calcSaving at bill ±100/200/300) ─────
    const scenariosFor = (plan) => {
        if (!Number.isFinite(bill) || bill <= 0) return [];
        const out = [];
        for (const step of SCENARIO_STEPS) {
            const sBill = bill + step;
            if (sBill <= 0) continue;
            const sUsage = Math.round(billToKwh(sBill));
            if (!sUsage || sUsage <= 0) continue;
            const sSaving = calcSaving(plan.kwp, sUsage, plan.battKwhTotal, sBill, dayPct);
            if (!Number.isFinite(sSaving)) continue;
            out.push({ bill: sBill, saving: sSaving, pct: sBill > 0 ? (sSaving / sBill) * 100 : 0 });
        }
        return out;
    };

    // ── Styles (match Price Checker / Savings Estimation) ────────────────
    const lblSx = { fontSize: '0.62rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: poppins, mb: 0.5 };
    // ── Input-panel tokens — now LIGHT to match Simple mode's white input column
    //    (user 2026-06-21). The `…Dark` names are kept (still referenced throughout
    //    the input panel JSX) but resolve to light styles on the white card. ──
    const lblSxDark = lblSx;
    const hintDark = { fontSize: '0.6rem', color: '#94A3B8', fontFamily: poppins, mt: 0.25, mb: 1.25 };
    const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: poppins, fontSize: '0.85rem' } };
    const fieldDark = {
        ...fieldSx,
        '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], bgcolor: '#F8FAFC', '& fieldset': { borderColor: '#E2E8F0' }, '&:hover fieldset': { borderColor: '#CBD5E1' }, '&.Mui-focused fieldset': { borderColor: '#1A8CFF' } },
        input: { color: '#0F172A' }, '& .MuiSelect-select': { color: '#0F172A' }, '& .MuiSvgIcon-root': { color: '#94A3B8' },
    };
    // Light toggle (matches Simple's input toggle): unselected light text + #E2E8F0
    // border, selected keeps the Nexwave blue gradient.
    const toggleSxDark = {
        bgcolor: '#F8FAFC',
        borderRadius: '8px',
        '& .MuiToggleButton-root': {
            textTransform: 'none', fontFamily: poppins, fontWeight: 700, fontSize: '0.7rem',
            color: '#64748B', border: '1px solid #E2E8F0', px: 1.25, py: 0.5,
            '&:hover': { bgcolor: '#EFF6FF' },
            '&.Mui-selected': {
                color: '#fff', borderColor: 'transparent',
                background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)',
                '&:hover': { background: 'linear-gradient(135deg,#0F62CC,#0a4a9e)' },
            },
        },
    };
    // Light checkbox style (promo list) on the white card.
    const checkSxDark = { color: '#94A3B8', '&.Mui-checked': { color: '#1A8CFF' }, '&.Mui-disabled': { color: '#E2E8F0' }, py: 0.25 };
    const promoLblSx = { fontSize: '0.68rem', fontWeight: 600, fontFamily: poppins, color: '#0F172A', lineHeight: 1.25 };
    const promoSubSx = { color: '#94A3B8', fontWeight: 400 };
    const rm = (v) => `RM ${Math.round(Number(v) || 0).toLocaleString()}`;
    const endKwh = (txt) => ({ endAdornment: <InputAdornment position="end"><Typography sx={{ fontFamily: poppins, fontSize: '0.78rem', color: '#64748B' }}>{txt}</Typography></InputAdornment> });

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(260px, 1fr) minmax(0, 3.2fr)' }, gap: 2, alignItems: 'start' }}>
            {/* ── Customer Profile (white input card — matches Simple mode) ───── */}
            <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 3, color: '#0F172A', alignSelf: 'start', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#0F62CC', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: poppins, mb: 1.5 }}>
                    {t('客户资料', 'Customer Profile', 'Profil Pelanggan')}
                </Typography>

                {/* Responsive 2-col grid (mirrors the reference's input-grid) */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1.25, rowGap: 0 }}>
                    {/* Meter Type (phase) — spans full width. SHARED phaseMode state. */}
                    <Box sx={{ gridColumn: { xs: '1', sm: '1 / span 2' } }}>
                        <Typography sx={lblSxDark}>{t('🔌 电表类型', '🔌 Meter Type', '🔌 Jenis Meter')}</Typography>
                        <ToggleButtonGroup size="small" exclusive value={phaseMode} onChange={(_, v) => v && setPhaseMode(v)} sx={{ ...toggleSxDark, mb: 1.25 }}>
                            <ToggleButton value="single">{t('单相', 'Single Phase', '1 Fasa')}</ToggleButton>
                            <ToggleButton value="3phase">{t('三相', 'Three Phase', '3 Fasa')}</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {/* Monthly TNB Bill (RM) */}
                    <Box>
                        <Typography sx={lblSxDark}>{t('💰 每月电费 (RM)', '💰 Monthly TNB Bill (RM)', '💰 Bil TNB Bulanan (RM)')}</Typography>
                        <TextField size="small" fullWidth type="number" placeholder={t('例如 1200', 'e.g. 1200', 'cth. 1200')} value={billRmStr}
                            onChange={e => onBillRmChange(e.target.value)} sx={fieldDark}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ fontFamily: poppins, fontSize: '0.78rem', color: '#64748B' }}>RM</Typography></InputAdornment> }} />
                        <Typography sx={hintDark}>{t('与用电量自动同步', 'Auto-syncs with usage', 'Auto-segerak dengan penggunaan')}</Typography>
                    </Box>

                    {/* Monthly Usage (kWh) */}
                    <Box>
                        <Typography sx={lblSxDark}>{t('⚡ 每月用电 (kWh)', '⚡ Monthly Usage (kWh)', '⚡ Penggunaan Bulanan (kWh)')}</Typography>
                        <TextField size="small" fullWidth type="number" placeholder={t('例如 1800', 'e.g. 1800', 'cth. 1800')} value={usageKwhStr}
                            onChange={e => onUsageKwhChange(e.target.value)} sx={fieldDark} InputProps={endKwh('kWh')} />
                        <Typography sx={hintDark}>{t('与电费自动同步', 'Auto-syncs with bill', 'Auto-segerak dengan bil')}</Typography>
                    </Box>

                    {/* Daytime Usage (%) */}
                    <Box>
                        <Typography sx={lblSxDark}>{t('☀ 白天用电 (%)', '☀ Daytime Usage (%)', '☀ Penggunaan Siang (%)')}</Typography>
                        <TextField size="small" fullWidth type="number" placeholder="30" value={dayPctStr}
                            onChange={e => setDayPctStr(e.target.value)} sx={fieldDark} InputProps={endKwh('%')} />
                        <Typography sx={hintDark}>{t('总用电中白天的比例', 'Share of consumption during daytime', 'Bahagian penggunaan waktu siang')}</Typography>
                    </Box>

                    {/* Max Roof Capacity (panels) */}
                    <Box>
                        <Typography sx={lblSxDark}>{t('🏠 屋顶板数上限', '🏠 Max Roof Capacity', '🏠 Kapasiti Bumbung')}</Typography>
                        <TextField size="small" fullWidth type="number" placeholder={t('选填', 'Optional', 'Pilihan')} value={panelLimitStr}
                            onChange={e => setPanelLimitStr(e.target.value)} sx={fieldDark} InputProps={endKwh(t('块', 'panels', 'panel'))} />
                        <Typography sx={hintDark}>{t('不确定可留空', 'Leave blank if unknown', 'Biar kosong jika tidak pasti')}</Typography>
                    </Box>

                    {/* Add-on package is now PER-CARD (user 2026-06-21) — each plan card
                        below carries its own editable add-on rows (AddonRowsEditor). */}

                    {/* Salesperson Name + Phone fields removed (user 2026-06-21) — not in
                        Simple mode's input either; the agent comes from the logged-in user. */}
                </Box>

                {bill > 0 && (
                    <Typography sx={{ fontSize: '0.66rem', color: '#64748B', fontFamily: poppins, mb: 1.25 }}>
                        {t('原电费 (C2 Tariff)', 'Original Bill (C2 Tariff)', 'Bil Asal (C2 Tariff)')}: <span style={{ fontWeight: 700, color: '#0F62CC' }}>{rm(bill)}</span>
                    </Typography>
                )}

                {/* ── Promotions — bound to the SHARED promo flags (the engine's
                    computePromoAmt / buildBreakdown / getPrice close over these), so
                    the priced numbers stay BYTE-IDENTICAL to Simple. ── */}
                <Box sx={{ mt: 0.5, p: 1.25, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                    <Typography sx={{ ...lblSxDark, mb: 0.75 }}>{t('促销', 'Promotions', 'Promosi')}</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        <FormControlLabel sx={{ ml: 0, alignItems: 'flex-start' }}
                            control={<Checkbox size="small" checked={promoBackupbox} onChange={e => setPromoBackupbox(e.target.checked)} sx={checkSxDark} />}
                            label={<Typography sx={promoLblSx}>Backupbox Removal <span style={promoSubSx}>(1P RM 1,600 / 3P RM 2,500)</span></Typography>} />
                        <FormControlLabel sx={{ ml: 0, alignItems: 'flex-start' }}
                            control={<Checkbox size="small" checked={promoFirst640} onChange={e => setPromoFirst640(e.target.checked)} sx={checkSxDark} />}
                            label={<Typography sx={promoLblSx}>FIRST640 Promo <span style={promoSubSx}>(box removed 1P 800/3P 1,600 · kept 1P 1,800/3P 3,000)</span></Typography>} />
                        <FormControlLabel sx={{ ml: 0, alignItems: 'flex-start' }}
                            control={<Checkbox size="small" checked={promoPerBatt} onChange={e => setPromoPerBatt(e.target.checked)} sx={checkSxDark} />}
                            label={<Typography sx={promoLblSx}>{t('每电池折扣', 'Per Battery Discount', 'Diskaun Per Bateri')} <span style={promoSubSx}>(RM 800 × 16kWh)</span></Typography>} />
                        <FormControlLabel sx={{ ml: 0, alignItems: 'flex-start' }}
                            control={<Checkbox size="small" checked={promoGovRebate} onChange={e => setPromoGovRebate(e.target.checked)} sx={checkSxDark} />}
                            label={<Typography sx={promoLblSx}>SuRIA Home Rebate <span style={promoSubSx}>(RM 600 {'×'} inverter kW, cap RM 3,000)</span></Typography>} />
                    </Box>
                    <Typography sx={{ fontSize: '0.58rem', color: '#94A3B8', fontFamily: poppins, mt: 0.5, fontStyle: 'italic' }}>
                        {t('与简易计算器完全一致；SuRIA 安装后向 TNB 申领，CC 按未扣回扣报价换算', 'Identical to the Simple calculator; SuRIA is claimed from TNB after install, CC converts the pre-rebate quote', 'Sama dengan kalkulator ringkas; SuRIA dituntut dari TNB selepas pasang, CC tukar harga sebelum rebat')}
                    </Typography>
                </Box>

                {/* Light Advanced Settings */}
                <Button size="small" startIcon={<TuneIcon sx={{ fontSize: '1rem' }} />} onClick={() => setShowAdv(v => !v)}
                    sx={{ mt: 1.25, textTransform: 'none', fontFamily: poppins, fontWeight: 700, fontSize: '0.68rem', color: '#0F62CC', px: 0 }}>
                    {t('高级设置', 'Advanced Settings', 'Tetapan Lanjutan')}
                    {showAdv ? <ExpandLessIcon sx={{ fontSize: '1rem', ml: 0.25 }} /> : <ExpandMoreIcon sx={{ fontSize: '1rem', ml: 0.25 }} />}
                </Button>
                <Collapse in={showAdv}>
                    <Box sx={{ mt: 1, p: 1.25, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                        <Typography sx={lblSxDark}>{t('最低电费减少 (%)', 'Min Bill Cut (%)', 'Potongan Bil Min (%)')}</Typography>
                        <TextField size="small" fullWidth type="number" placeholder={t('不限', 'No floor', 'Tiada had')} value={minBillCut}
                            onChange={e => setMinBillCut(e.target.value)} sx={{ ...fieldDark, mb: 1 }} InputProps={endKwh('%')} />
                        {['fastest', 'highValue', 'max'].map(key => (
                            <Box key={key} sx={{ mb: 1 }}>
                                <Typography sx={{ ...lblSxDark, color: PICK_META[key].accent }}>{t(PICK_META[key].cn, PICK_META[key].en, PICK_META[key].my)}</Typography>
                                <ToggleButtonGroup size="small" exclusive value={battModes[key]} onChange={(_, v) => v && setBattModes(p => ({ ...p, [key]: v }))} sx={{ ...toggleSxDark }}>
                                    <ToggleButton value="any">{t('任意', 'Any', 'Mana-mana')}</ToggleButton>
                                    <ToggleButton value="with">{t('有电池', 'Battery', 'Bateri')}</ToggleButton>
                                    <ToggleButton value="without">{t('无电池', 'No Batt', 'Tiada')}</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        ))}
                    </Box>
                </Collapse>
            </Box>

            {/* ── Results (light card) ──────────────────────────────── */}
            <Box>
                {!hasResults ? (
                    // No bill yet → show the 3 dark plan-card placeholders with a simple
                    // "enter usage" message inside (mirrors Simple's empty Option A/B cards).
                    // Bill entered but nothing viable → the adjust-config hint instead.
                    usageKwh > 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: 3 }}>
                            <Typography sx={{ fontFamily: poppins, fontSize: '0.85rem', color: '#64748B' }}>
                                {t('此配置下没有可行方案，请调整用电、板数上限或高级设置', 'No viable plan for this setup — adjust usage, panel limit or advanced settings', 'Tiada pelan untuk tetapan ini — laras penggunaan / had panel / tetapan lanjutan')}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                            {['fastest', 'highValue', 'max'].map(key => (
                                <Box key={key} sx={{ background: PICK_META[key].bg, borderRadius: 3, minHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                                    <Typography sx={{ fontFamily: poppins, fontSize: '0.85rem', fontWeight: 600, color: 'rgba(255,255,255,0.7)', textAlign: 'center' }}>
                                        {t('请先输入用电量', 'Please enter usage first', 'Sila masukkan penggunaan dahulu')}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )
                ) : (<>
                    {/* Action bar removed (user 2026-06-21): the "复制 WhatsApp" view-level
                        button was dropped, and "10 年对比" moved into the export chooser
                        (renders inline like 图表). */}

                    {/* Plan cards (3 recommended plans) — each is now an interactive
                        stateful card with Battery / Panels / Inverter steppers and a
                        CHECKBOX (Change 1) for multi-select. */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                        {picks.map((plan, cardIdx) => {
                            const selIdx = selectedKeys.indexOf(plan.key);
                            return (
                                <PlanCard
                                    key={plan.key}
                                    plan={plan}
                                    sortedRows={sortedRows}
                                    batt16={batt16}
                                    hydrateConfig={hydrateConfig}
                                    promoBackupbox={promoBackupbox}
                                    promoGovRebate={promoGovRebate}
                                    phaseMode={phaseMode}
                                    isOpen={!!expanded[plan.key]}
                                    onToggle={() => setExpanded(p => ({ ...p, [plan.key]: !p[plan.key] }))}
                                    scenariosFor={scenariosFor}
                                    bill={bill}
                                    t={t}
                                    poppins={poppins}
                                    lblSx={lblSx}
                                    rm={rm}
                                    buildPlanDraft={buildPlanDraft}
                                    isSelected={selIdx >= 0}
                                    optLetter={String.fromCharCode(65 + cardIdx)}
                                    onTogglePlan={togglePlan}
                                    onDraftChange={updatePlanDraft}
                                    onLiveChange={updatePlanLive}
                                    onViableChange={updatePlanViable}
                                    onOpenCompare={() => { setCompareFocusKey(plan.key); setShowCompare(true); }}
                                    addons={addons}
                                    phaseLabel={phaseLabel}
                                    addonRows={rowsForKey(plan.key)}
                                    onAddAddon={(addonId) => addAddonRow(plan.key, addonId)}
                                    onUpdateAddon={(rowId, patch) => updateAddonRow(plan.key, rowId, patch)}
                                    onRemoveAddon={(rowId) => removeAddonRow(plan.key, rowId)}
                                    onToggleShortcut={(re) => toggleShortcutAddon(plan.key, re)}
                                />
                            );
                        })}
                    </Box>

                    {/* ── Export chooser (Change 2) — mirrors Simple's "选择导出" bar.
                        Opt A = 1st selected plan · Opt B = 2nd selected · A+B = both ·
                        图表 = comparison of the selected plans. Buttons disable until
                        enough plans are selected. ── */}
                    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', p: '10px 14px', bgcolor: '#fff', borderRadius: 3, border: '1px solid #E2E8F0' }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: poppins, mr: 0.5 }}>
                            {t('选择导出', 'Export', 'Eksport')}:
                        </Typography>
                        {/* One "Opt {letter}" button per SELECTED plan (fixed A/B/C by card
                            position), plus a combo of all selected (e.g. "B+C"). Follows
                            the selection — if B+C are ticked, you get Opt B · Opt C · B+C. */}
                        {selectedInCardOrder.map((plan) => {
                            const id = plan.key;
                            const letter = letterForKey(plan.key);
                            const color = PICK_META[plan.key]?.accent || '#0EA5E9';
                            return (
                                <Button key={id} size="small" onClick={() => onPickExport(id)}
                                    variant={activeExport === id ? 'contained' : 'outlined'}
                                    sx={{ fontFamily: poppins, fontSize: '0.78rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none',
                                        ...(activeExport === id
                                            ? { bgcolor: color, color: '#fff', borderColor: color, '&:hover': { bgcolor: color } }
                                            : { borderColor: `${color}60`, color, '&:hover': { bgcolor: `${color}10`, borderColor: color } }) }}>
                                    Opt {letter}
                                </Button>
                            );
                        })}
                        {selectedInCardOrder.length >= 2 && (
                            <Button size="small" onClick={() => onPickExport('combo')}
                                variant={activeExport === 'combo' ? 'contained' : 'outlined'}
                                sx={{ fontFamily: poppins, fontSize: '0.78rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none',
                                    ...(activeExport === 'combo'
                                        ? { bgcolor: '#6B7280', color: '#fff', borderColor: '#6B7280', '&:hover': { bgcolor: '#6B7280' } }
                                        : { borderColor: '#6B728060', color: '#6B7280', '&:hover': { bgcolor: '#6B728010', borderColor: '#6B7280' } }) }}>
                                {comparisonLetters.join('+')}
                            </Button>
                        )}
                        {/* 图表 — toggles the inline summary comparison table below
                            (replaces the old 10-Year modal open). Active = filled. */}
                        <Button size="small" disabled={selectedKeys.length < 1}
                            onClick={() => onPickExport('chart')}
                            variant={activeExport === 'chart' ? 'contained' : 'outlined'}
                            sx={{ fontFamily: poppins, fontSize: '0.78rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none',
                                ...(activeExport === 'chart'
                                    ? { bgcolor: '#0F62CC', color: '#fff', borderColor: '#0F62CC', '&:hover': { bgcolor: '#0F62CC' } }
                                    : { borderColor: '#0F62CC60', color: '#0F62CC', '&:hover': { bgcolor: '#0F62CC10', borderColor: '#0F62CC' } }),
                                '&.Mui-disabled': { borderColor: '#E2E8F0', color: '#CBD5E1', bgcolor: 'transparent' } }}>
                            {t('图表', 'Chart', 'Carta')}
                        </Button>
                        {/* 10年对比 — moved here from the action bar; renders the 10-year
                            comparison INLINE (preview) like 图表. */}
                        <Button size="small" disabled={selectedKeys.length < 1}
                            onClick={() => onPickExport('tenyear')}
                            variant={activeExport === 'tenyear' ? 'contained' : 'outlined'}
                            sx={{ fontFamily: poppins, fontSize: '0.78rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none',
                                ...(activeExport === 'tenyear'
                                    ? { bgcolor: '#1A8CFF', color: '#fff', borderColor: '#1A8CFF', '&:hover': { bgcolor: '#1A8CFF' } }
                                    : { borderColor: '#1A8CFF60', color: '#1A8CFF', '&:hover': { bgcolor: '#1A8CFF10', borderColor: '#1A8CFF' } }),
                                '&.Mui-disabled': { borderColor: '#E2E8F0', color: '#CBD5E1', bgcolor: 'transparent' } }}>
                            {t('10年对比', '10-Year', '10 Tahun')}
                        </Button>
                        {/* Create Sales now lives in the shared Section 2 below (the active
                            Opt chosen here drives that Section's PDF Forms / Quotation / ATAP
                            / Create-Sales via onAdvActiveOpt → buildResidentialFormPayload).
                            The old per-chooser green "创建销售" button was removed. */}
                        {selectedKeys.length === 0 && (
                            <Typography sx={{ fontSize: '0.64rem', color: '#94A3B8', fontFamily: poppins, fontStyle: 'italic', ml: 0.5 }}>
                                {t('勾选方案以导出', 'Tick plans to export', 'Tanda pelan untuk eksport')}
                            </Typography>
                        )}
                    </Box>

                    {/* Customizable WhatsApp (user 2026-06-21) — same editable template as
                        Simple (shared). Custom A/B/A+B fills it with each selected plan's
                        LIVE numbers. Text-only — the package/Create-Sales flow is unaffected. */}
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', p: '10px 14px', bgcolor: '#fff', borderRadius: 3, border: '1px solid #E2E8F0' }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: poppins, mr: 0.5 }}>{t('自定义信息', 'Custom', 'Tersuai')}:</Typography>
                        {(() => {
                            const TEAL = '#0D9488';
                            const btnSx = (on) => ({ fontFamily: poppins, fontSize: '0.78rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', ...(on ? { bgcolor: TEAL, color: '#fff', borderColor: TEAL, '&:hover': { bgcolor: '#0b7a6f' } } : { borderColor: `${TEAL}60`, color: TEAL, '&:hover': { bgcolor: `${TEAL}10`, borderColor: TEAL } }) });
                            return (<>
                                {selectedInCardOrder.map((plan) => {
                                    const ck = 'custom_' + plan.key;
                                    return <Button key={plan.key} size="small" onClick={() => onPickCustom(ck)} variant={activeExport === ck ? 'contained' : 'outlined'} sx={btnSx(activeExport === ck)}>{t('自定义', 'Custom', 'Custom')} {letterForKey(plan.key)}</Button>;
                                })}
                                {selectedInCardOrder.length >= 2 && <Button size="small" onClick={() => onPickCustom('custom_combo')} variant={activeExport === 'custom_combo' ? 'contained' : 'outlined'} sx={btnSx(activeExport === 'custom_combo')}>{t('自定义', 'Custom', 'Custom')} {comparisonLetters.join('+')}</Button>}
                                <Box sx={{ flexGrow: 1 }} />
                                <Button size="small" onClick={() => setShowWaTemplate(v => !v)} variant="text" sx={{ fontFamily: poppins, fontSize: '0.74rem', fontWeight: 700, textTransform: 'none', color: '#475569', '&:hover': { bgcolor: '#F1F5F9' } }}>{showWaTemplate ? t('收起模板', 'Hide Template', 'Tutup') : t('编辑模板', 'Edit Template', 'Edit')}</Button>
                            </>);
                        })()}
                    </Box>

                    {/* Editable template (shared with Simple via localStorage). */}
                    {showWaTemplate && (
                        <Box sx={{ mt: 1, p: '12px 14px', bgcolor: '#fff', borderRadius: 3, border: '1px solid #E2E8F0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#0F172A', fontFamily: poppins }}>{t('自定义 WhatsApp 模板', 'Custom WhatsApp Template', 'Templat WhatsApp Tersuai')}</Typography>
                                <Button size="small" onClick={() => saveWaTemplate(DEFAULT_WA_TEMPLATE)} variant="outlined" sx={{ fontFamily: poppins, fontSize: '0.7rem', fontWeight: 600, textTransform: 'none', borderRadius: '8px', borderColor: '#E2E8F0', color: '#64748B', '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' } }}>{t('恢复默认', 'Reset to Default', 'Set Semula')}</Button>
                            </Box>
                            <TextField multiline minRows={7} fullWidth value={customWaTemplate} onChange={e => saveWaTemplate(e.target.value)}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#F8FAFC', fontFamily: "'JetBrains Mono',monospace", fontSize: '0.74rem' } }} />
                            <Typography sx={{ fontSize: '0.64rem', color: '#64748B', fontFamily: poppins, mt: 0.75, mb: 0.4 }}>{t('可用变量（点击插入）', 'Available placeholders (click to insert)', 'Pemegang tempat (klik untuk masukkan)')}:</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {WA_TEMPLATE_PLACEHOLDERS.map(ph => (
                                    <Box key={ph} onClick={() => saveWaTemplate(`${customWaTemplate}{${ph}}`)}
                                        sx={{ cursor: 'pointer', px: 0.75, py: 0.25, borderRadius: '6px', bgcolor: '#EFF6FF', border: '1px solid #BFDBFE', fontFamily: "'JetBrains Mono',monospace", fontSize: '0.66rem', color: '#0F62CC', '&:hover': { bgcolor: '#DBEAFE' } }}>
                                        {`{${ph}}`}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Summary comparison table (图表) — renders INLINE below the export
                        chooser when 图表 is active (one column per selected plan). Mirrors
                        the reference screenshot: purple-gradient header card + a metric
                        table built from the LIVE (stepper-adjusted) selected plans. */}
                    {activeExport === 'chart' && comparisonPlans.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                            <Box ref={chartRef}>
                                <ComparisonTable
                                    plans={comparisonPlans}
                                    letters={comparisonLetters}
                                    bill={bill}
                                    usageKwh={usageKwh}
                                    dayPctNum={dayPctNum}
                                    phaseMode={phaseMode}
                                    panelLimitStr={panelLimitStr}
                                    t={t}
                                    poppins={poppins}
                                    rm={rm}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                                <Button onClick={downloadChart} variant="outlined" size="small" startIcon={<DownloadIcon sx={{ fontSize: '0.9rem' }} />}
                                    sx={{ fontFamily: poppins, fontSize: '0.76rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', borderColor: '#0F62CC', color: '#0F62CC', '&:hover': { bgcolor: '#0F62CC10', borderColor: '#0F62CC' } }}>
                                    {t('下载', 'Download', 'Muat Turun')}
                                </Button>
                                <Button onClick={shareChart} variant="contained" size="small" startIcon={<WhatsAppIcon sx={{ fontSize: '0.9rem' }} />}
                                    sx={{ fontFamily: poppins, fontSize: '0.76rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', bgcolor: '#16A34A', '&:hover': { bgcolor: '#15803D' } }}>
                                    {t('分享', 'Share', 'Kongsi')}
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* 10-Year comparison — renders INLINE (preview) like 图表, with the
                        same Download / Share. Reuses chartRef (only one export preview is
                        mounted at a time, so it captures whichever is active). */}
                    {activeExport === 'tenyear' && tenYearPlans.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                            <Box ref={chartRef} sx={{ bgcolor: '#fff', borderRadius: 3, border: '1px solid #E2E8F0', p: 2 }}>
                                <Typography sx={{ fontFamily: poppins, fontWeight: 800, fontSize: '0.95rem', color: '#0F172A', mb: 1 }}>
                                    {t('10 年财务对比', '10-Year Financial Comparison', 'Perbandingan Kewangan 10 Tahun')}
                                </Typography>
                                <TenYearComparisonBody picks={tenYearPlans} letters={comparisonLetters} bill={bill} ccFromCash={ccFromCash} t={t} poppins={poppins} />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                                <Button onClick={downloadChart} variant="outlined" size="small" startIcon={<DownloadIcon sx={{ fontSize: '0.9rem' }} />}
                                    sx={{ fontFamily: poppins, fontSize: '0.76rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', borderColor: '#0F62CC', color: '#0F62CC', '&:hover': { bgcolor: '#0F62CC10', borderColor: '#0F62CC' } }}>
                                    {t('下载', 'Download', 'Muat Turun')}
                                </Button>
                                <Button onClick={shareChart} variant="contained" size="small" startIcon={<WhatsAppIcon sx={{ fontSize: '0.9rem' }} />}
                                    sx={{ fontFamily: poppins, fontSize: '0.76rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', bgcolor: '#16A34A', '&:hover': { bgcolor: '#15803D' } }}>
                                    {t('分享', 'Share', 'Kongsi')}
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* WhatsApp preview (Change 2) — shows the chosen export message.
                        Mirrors Simple's preview box + Copy / Open WhatsApp buttons.
                        Hidden when 图表 / 10年对比 is active (those render their own preview). */}
                    {activeExport && activeExport !== 'chart' && activeExport !== 'tenyear' && (
                        <Box sx={{ mt: 1.5, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                            <Box sx={{ bgcolor: '#075e54', p: '8px 14px', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WhatsAppIcon sx={{ color: '#fff', fontSize: '1.1rem' }} />
                                <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.8rem', fontFamily: poppins }}>
                                    {t('WhatsApp 预览', 'WhatsApp Preview', 'Pratonton WhatsApp')}
                                    {activeExport === 'combo' ? ` · ${comparisonLetters.join('+')}` : ` · Opt ${letterForKey(activeExport)}`}
                                </Typography>
                            </Box>
                            <Box sx={{ bgcolor: '#e5ddd5', minHeight: 100, p: 1.5 }}>
                                <Box sx={{ bgcolor: '#fff', borderRadius: '0 8px 8px 8px', p: 1.25, maxWidth: '92%', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                    <Typography component="pre" sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.7rem', lineHeight: 1.45, color: generatedMsg ? '#374151' : '#9CA3AF', fontStyle: generatedMsg ? 'normal' : 'italic', whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
                                        {generatedMsg || t('选择上方选项查看信息', 'Pick an option above to view the message', 'Pilih pilihan di atas untuk lihat mesej')}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ p: '8px 14px', borderTop: '1px solid #E2E8F0', bgcolor: '#F8FAFC', display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                                <Button onClick={() => copyMsg(generatedMsg)} disabled={!generatedMsg} variant="outlined" size="small" startIcon={<ContentCopyIcon sx={{ fontSize: '0.85rem' }} />}
                                    sx={{ fontFamily: poppins, fontSize: '0.76rem', fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}>
                                    {t('复制信息', 'Copy Message', 'Salin Mesej')}
                                </Button>
                                <Button onClick={() => openWAText(generatedMsg)} disabled={!generatedMsg} variant="contained" size="small" startIcon={<WhatsAppIcon sx={{ fontSize: '0.85rem' }} />}
                                    sx={{ fontFamily: poppins, fontSize: '0.76rem', fontWeight: 600, textTransform: 'none', borderRadius: '8px', background: 'linear-gradient(135deg,#25D366,#128C7E)', boxShadow: 'none', '&:hover': { background: 'linear-gradient(135deg,#20b558,#0f7360)', boxShadow: 'none' } }}>
                                    {t('开启 WhatsApp', 'Open WhatsApp', 'Buka WhatsApp')}
                                </Button>
                                {/* No Create Sales here — the sale is created from Section 2
                                    below (fill customer details first), per user 2026-06-21. */}
                            </Box>
                        </Box>
                    )}

                    {/* Salesperson footer (mirrors the reference's brand/sales card) */}
                    {(salesName || salesPhone) && (
                        <Box sx={{ mt: 1.5, p: 1.25, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: poppins }}>{t('销售顾问', 'Salesperson', 'Jurujual')}</Typography>
                            <Divider orientation="vertical" flexItem />
                            {salesName && <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0F172A', fontFamily: poppins }}>{salesName}</Typography>}
                            {salesPhone && <Typography sx={{ fontSize: '0.72rem', color: '#475569', fontFamily: poppins }}>{salesPhone}</Typography>}
                        </Box>
                    )}
                </>)}
            </Box>

            {/* ── 10-Year Comparison modal (Change 3) ─────────────────────────── */}
            {/* When opened from a card, compareFocusKey narrows to that single plan.
                Otherwise (the 图表 export button / view-level "10-Year" button) the
                modal compares the SELECTED plans; if nothing is selected yet it falls
                back to all ranked picks so the view-level button still works. */}
            <TenYearComparisonModal
                open={showCompare}
                onClose={() => { setShowCompare(false); setCompareFocusKey(null); }}
                picks={
                    compareFocusKey
                        ? picks.filter(p => p.key === compareFocusKey)
                        : (selectedInCardOrder.length ? selectedInCardOrder : picks)
                }
                bill={bill}
                ccFromCash={ccFromCash}
                t={t}
                poppins={poppins}
            />

            <Snackbar open={!!snack} autoHideDuration={2500} onClose={() => setSnack('')} message={snack}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
        </Box>
    );
}

/**
 * Summary comparison table for the 图表 export — one column per selected plan.
 * Reads the LIVE (stepper-adjusted) display fields each plan carries:
 *   panels, kwp, battQty, battKwhEach, monthlySaving, savingsPct,
 *   netCash (preRebateCash), cc[36] (36-mo 0% installment TOTAL),
 *   roiYears, inverterLabel / effInvKw.
 * Styled like the reference screenshot: white card, purple gradient header,
 * tinted metric column, alternating row backgrounds.
 */
function ComparisonTable({ plans, letters, bill, usageKwh, dayPctNum, phaseMode, panelLimitStr, t, poppins, rm }) {
    if (!plans || !plans.length) return null;
    const is3p = phaseMode === '3phase';
    const meterLabel = is3p ? t('三相电表', 'Three-phase Meter', 'Meter 3 Fasa') : t('单相电表', 'Single-phase Meter', 'Meter 1 Fasa');
    const roofLabel = parseInt(panelLimitStr, 10) > 0
        ? t(`屋顶可容纳电板: ${parseInt(panelLimitStr, 10)} 片`, `Roof capacity: ${parseInt(panelLimitStr, 10)} panels`, `Kapasiti bumbung: ${parseInt(panelLimitStr, 10)} panel`)
        : t('屋顶可容纳电板: 待确认', 'Roof capacity: TBC', 'Kapasiti bumbung: TBD');

    // Per-plan value helpers (live object OR pick — same field shape).
    const cc36 = (p) => (p.cc && p.cc[36] != null) ? p.cc[36] : 0;
    const invLabel = (p) => {
        if (p.effInvKw && p.naturalInvKw && p.effInvKw !== p.naturalInvKw) return `${p.effInvKw}kW`;
        return p.inverterLabel || (p.effInvKw ? `${p.effInvKw}kW` : '—');
    };

    // Row definitions: label (via t) + a per-plan value function.
    const rows = [
        { label: t('系统配置', 'System', 'Sistem'),
          val: (p) => t(`${p.panels}片 (${p.kwp} kWp)`, `${p.panels} panels (${p.kwp} kWp)`, `${p.panels} panel (${p.kwp} kWp)`) },
        { label: t('电池', 'Battery', 'Bateri'),
          val: (p) => p.battQty > 0
              ? t(`${p.battQty}粒电池 (${p.battKwhEach}kWh)`, `${p.battQty}× battery (${p.battKwhEach}kWh)`, `${p.battQty} bateri (${p.battKwhEach}kWh)`)
              : t('无电池', 'No Battery', 'Tiada Bateri') },
        { label: t('每月节省', 'Monthly Saving', 'Jimat Bulanan'), highlight: true,
          val: (p) => rm(p.monthlySaving) },
        { label: t('节省比例', 'Saving %', 'Jimat %'),
          val: (p) => `${p.savingsPct}%` },
        { label: t('现金价', 'Cash Price', 'Harga Tunai'),
          val: (p) => rm(p.netCash != null ? p.netCash : p.preRebateCash) },
        { label: t('0%分期总额', '0% Installment Total', 'Jumlah Ansuran 0%'),
          val: (p) => rm(cc36(p)) },
        { label: t('0%分期 (36个月)', '0% Installment (36mo)', 'Ansuran 0% (36 bln)'),
          val: (p) => t(`${rm(cc36(p) / 36)}/月`, `${rm(cc36(p) / 36)}/mo`, `${rm(cc36(p) / 36)}/bln`) },
        { label: t('回本期', 'ROI', 'ROI'),
          val: (p) => Number.isFinite(p.roiYears) ? t(`${p.roiYears} 年`, `${p.roiYears} yrs`, `${p.roiYears} thn`) : '—' },
        { label: t('逆变器', 'Inverter', 'Inverter'),
          val: (p) => invLabel(p) },
    ];

    // Column header = the plan's FIXED letter (方案 B / 方案 C …), falling back to index.
    const colHead = (i) => {
        const L = letters && letters[i] ? letters[i] : String.fromCharCode(65 + i);
        return t(`方案 ${L}`, `Plan ${L}`, `Pelan ${L}`);
    };
    const cellSx = { fontFamily: poppins, fontSize: '0.72rem', color: '#0F172A', p: '8px 10px', textAlign: 'center', borderBottom: '1px solid #EEF2F7' };
    const metricSx = { fontFamily: poppins, fontSize: '0.72rem', fontWeight: 700, color: '#475569', p: '8px 10px', textAlign: 'left', bgcolor: '#EFF6FF', borderBottom: '1px solid #EEF2F7', borderRight: '1px solid #EEF2F7', whiteSpace: 'nowrap' };

    return (
        <Box sx={{ mt: 1.5, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden', boxShadow: '0 4px 14px rgba(15,23,42,0.06)' }}>
            {/* Purple gradient header */}
            <Box sx={{ background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)', p: '14px 16px' }}>
                <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.95rem', fontFamily: poppins, lineHeight: 1.3 }}>
                    {t('Astern 住宅方案对比', 'Astern Residential Comparison', 'Perbandingan Pelan Kediaman Astern')}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.64rem', fontFamily: poppins, mt: 0.5, lineHeight: 1.4 }}>
                    {meterLabel}
                    {bill > 0 ? ` · ${t('每月电费', 'Monthly bill', 'Bil bulanan')} ${rm(bill)}` : ''}
                    {usageKwh > 0 ? ` · ${t('每月用电量约', 'Monthly usage ~', 'Penggunaan bulanan ~')} ${Math.round(usageKwh).toLocaleString()} kWh` : ''}
                    {` · ${t('白天用电比例', 'Daytime usage', 'Penggunaan siang')} ${Math.round(dayPctNum)}%`}
                    {` · ${roofLabel}`}
                </Typography>
            </Box>

            {/* Table */}
            <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <Box component="thead">
                        <Box component="tr">
                            <Box component="th" sx={{ ...metricSx, bgcolor: '#DBEAFE', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#0F62CC' }}>
                                {t('指标', 'Metric', 'Metrik')}
                            </Box>
                            {plans.map((p, i) => (
                                <Box component="th" key={i} sx={{ ...cellSx, bgcolor: '#F8F7FF', fontWeight: 800, fontSize: '0.72rem', color: '#0F62CC', borderBottom: '1px solid #E9D5FF' }}>
                                    {colHead(i)}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {rows.map((r, ri) => (
                            <Box component="tr" key={ri} sx={{ bgcolor: ri % 2 === 0 ? '#fff' : '#FAFAFE' }}>
                                <Box component="td" sx={metricSx}>{r.label}</Box>
                                {plans.map((p, i) => (
                                    <Box component="td" key={i}
                                        sx={{ ...cellSx, ...(r.highlight ? { fontWeight: 800, color: '#16A34A' } : {}) }}>
                                        {r.val(p)}
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

// ── Compact +/- stepper (mirrors the Astern System Recommendation card) ──────
function Stepper({ label, value, onDec, onInc, canDec, canInc, accent, poppins }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 0.5, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2 }}>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: poppins }}>{label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <IconButton size="small" onClick={onDec} disabled={!canDec} sx={{ width: 22, height: 22, border: '1px solid rgba(255,255,255,0.15)', borderRadius: 1.5, color: '#fff', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}>
                    <RemoveIcon sx={{ fontSize: '0.85rem' }} />
                </IconButton>
                <Typography sx={{ minWidth: 56, textAlign: 'center', fontFamily: "'JetBrains Mono',monospace", fontSize: '0.66rem', fontWeight: 700, color: '#fff' }}>{value}</Typography>
                <IconButton size="small" onClick={onInc} disabled={!canInc} sx={{ width: 22, height: 22, border: '1px solid rgba(255,255,255,0.15)', borderRadius: 1.5, color: '#fff', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}>
                    <AddIcon sx={{ fontSize: '0.85rem' }} />
                </IconButton>
            </Box>
        </Box>
    );
}

/**
 * Per-card add-on rows editor (user 2026-06-21) — light-themed (fits the white
 * plan card) but mirrors Simple's per-option add-on table EXACTLY in structure:
 * the `1fr 50px 1fr 22px` grid, Base/Rate split inputs, quick-add shortcut buttons,
 * and a grouped "Add from catalog…" picker. Each card owns its own `rows`, so a
 * small plan and a big plan can carry different accessories. Inverter-upgrade items
 * are excluded from the catalog picker (the card's stepper drives those).
 */
function AddonRowsEditor({ rows, addons, phaseLabel, previewPanels, poppins, t, onAdd, onUpdate, onRemove, onToggleShortcut }) {
    // Light-card input styling (Nexwave blue focus), matching the plan card palette.
    const rowInputSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '6px', bgcolor: 'rgba(255,255,255,0.06)', height: 30,
            '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
            '&.Mui-focused fieldset': { borderColor: '#93C5FD' },
        },
        '& .MuiOutlinedInput-input': { padding: '4px 6px', color: '#fff', fontFamily: poppins, fontSize: '0.74rem', fontWeight: 600 },
        '& .MuiSelect-select': { color: '#fff', py: '4px', pl: '8px' },
        '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.6)' },
        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
        '& input[type=number]': { MozAppearance: 'textfield' },
    };
    const grid = '1fr 50px 1fr 22px';
    return (
        <Box sx={{ mt: 0.5, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2, p: 1 }}>
            {rows.length === 0 && (
                <Typography sx={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.6)', fontFamily: poppins, fontStyle: 'italic', textAlign: 'center', py: 0.5 }}>
                    {t('暂无配件 — 从下方目录选择', 'No add-ons yet — pick from catalog below', 'Tiada tambahan — pilih dari katalog di bawah')}
                </Typography>
            )}
            {rows.length > 0 && (
                <Box sx={{ display: 'grid', gridTemplateColumns: grid, gap: 0.5, alignItems: 'center', pb: 0.4, borderBottom: '1px dashed rgba(255,255,255,0.15)' }}>
                    <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: poppins }}>{t('项目', 'Item', 'Item')}</Typography>
                    <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: poppins, textAlign: 'center' }}>{t('数量', 'Qty', 'Kuan')}</Typography>
                    <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: poppins, textAlign: 'center' }}>{t('价格', 'Price', 'Harga')}</Typography>
                    <span />
                </Box>
            )}
            {rows.map((r) => {
                const isPerPanel = r.unitType === 'PerPanel';
                const qtyShown = r._qty != null ? r._qty : (isPerPanel ? previewPanels : 1);
                const line = computeAddonAmount(r, Number(qtyShown) || 0);
                const unitTag = r.unitType === 'PerPanel' ? '/panel' : r.unitType === 'PerYear' ? '/year' : r.unitType === 'PerItem' ? '/item' : '';
                const rawPrice = String(r.price ?? '');
                const cleanedPrice = rawPrice.replace(/[^\d.+]/g, '');
                let baseStr = '', rateStr = '';
                if (cleanedPrice.includes('+')) {
                    const parts = cleanedPrice.split('+');
                    baseStr = parts[0] || ''; rateStr = parts[1] || '';
                } else { rateStr = cleanedPrice; }
                const baseNum = parseFloat(baseStr) || 0;
                const rateNum = parseFloat(rateStr) || 0;
                const showBase = baseNum > 0;
                const showRate = rateNum > 0 || baseNum === 0;
                const setPriceParts = (newBaseRaw, newRateRaw) => {
                    const b = (newBaseRaw || '').toString().replace(/[^\d.]/g, '');
                    const rt = (newRateRaw || '').toString().replace(/[^\d.]/g, '');
                    const combined = (parseFloat(b) > 0) ? (rt === '' ? b : `${b}+${rt}`) : rt;
                    onUpdate(r.rowId, { price: combined });
                };
                return (
                    <Box key={r.rowId || r.addonId} sx={{ display: 'grid', gridTemplateColumns: grid, gap: 0.5, alignItems: 'center', py: 0.5, borderBottom: '1px dashed rgba(255,255,255,0.1)' }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#fff', fontFamily: poppins, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.itemName}>{r.itemName}</Typography>
                                {unitTag && <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.6)', fontFamily: poppins, fontWeight: 600, flexShrink: 0 }}>{unitTag}</Typography>}
                            </Box>
                            {line.total > 0 && (
                                <Typography sx={{ fontSize: '0.6rem', color: '#86EFAC', fontFamily: "'JetBrains Mono',monospace", fontWeight: 600, mt: 0.1 }}>
                                    {(line.base > 0 && line.perUnit > 0)
                                        ? `RM ${line.base.toLocaleString()} + ${qtyShown}× RM ${line.perUnit.toLocaleString()} = RM ${line.total.toLocaleString()}`
                                        : `= RM ${line.total.toLocaleString()}`}
                                </Typography>
                            )}
                        </Box>
                        <TextField size="small" type="number"
                            value={r._qty ?? (isPerPanel ? previewPanels : 1)}
                            onChange={e => onUpdate(r.rowId, { _qty: e.target.value === '' ? '' : parseFloat(e.target.value) || 0 })}
                            inputProps={{ step: 1, min: 0 }}
                            sx={{ ...rowInputSx, '& .MuiOutlinedInput-input': { ...rowInputSx['& .MuiOutlinedInput-input'], textAlign: 'center' } }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                            {showBase && (
                                <TextField size="small" type="number" value={baseStr}
                                    onChange={e => setPriceParts(e.target.value, rateStr)} placeholder="0" inputProps={{ step: 1, min: 0 }}
                                    sx={{ ...rowInputSx, '& .MuiOutlinedInput-input': { ...rowInputSx['& .MuiOutlinedInput-input'], textAlign: 'right', fontSize: '0.7rem' } }}
                                    InputProps={showRate ? { startAdornment: <Typography sx={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.6)', fontFamily: poppins, fontWeight: 600, mr: 0.4 }}>Base</Typography> } : undefined} />
                            )}
                            {showRate && (
                                <TextField size="small" type="number" value={rateStr}
                                    onChange={e => setPriceParts(baseStr, e.target.value)} placeholder="0" inputProps={{ step: 1, min: 0 }}
                                    sx={{ ...rowInputSx, '& .MuiOutlinedInput-input': { ...rowInputSx['& .MuiOutlinedInput-input'], textAlign: 'right', fontSize: '0.7rem' } }} />
                            )}
                        </Box>
                        <IconButton size="small" onClick={() => onRemove(r.rowId)} sx={{ p: 0.25, color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#FCA5A5', bgcolor: 'rgba(239,68,68,0.15)' } }}>
                            <CloseIcon sx={{ fontSize: '0.85rem' }} />
                        </IconButton>
                    </Box>
                );
            })}
            {/* Quick-add shortcuts — Nexwave-blue outlined, "+ {name}" toggle (add OR remove). */}
            {(() => {
                const pool = (addons || []).filter(a => addonMatchesPhase(a, phaseLabel) && !isCommercialAddon(a));
                const btns = ADDON_SHORTCUTS.map(s => ({ s, a: pool.find(x => s.re.test(x.itemName || '')) })).filter(x => x.a);
                if (!btns.length) return null;
                return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                        {btns.map(({ s, a }) => (
                            <Button key={s.key} size="small" variant="outlined" onClick={() => onToggleShortcut(s.re)}
                                sx={{ fontFamily: poppins, fontSize: '0.68rem', fontWeight: 600, textTransform: 'none', borderRadius: '8px', py: 0.2, px: 1, minWidth: 0, lineHeight: 1.3, color: '#93C5FD', borderColor: 'rgba(255,255,255,0.25)', '&:hover': { borderColor: '#93C5FD', bgcolor: 'rgba(255,255,255,0.08)' } }}>
                                {'+ '}{t(s.label_cn, s.label, s.label_my)}
                            </Button>
                        ))}
                    </Box>
                );
            })()}
            {/* Catalog picker — grouped by category; inverter-upgrade items excluded. */}
            <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <Select size="small" fullWidth displayEmpty value=""
                    onChange={e => { if (e.target.value) onAdd(Number(e.target.value)); }}
                    sx={rowInputSx} MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}>
                    <MenuItem value="" disabled sx={{ fontSize: '0.72rem', fontFamily: poppins, fontStyle: 'italic' }}>{'+ '}{t('从目录添加配件…', 'Add from catalog…', 'Tambah dari katalog…')}</MenuItem>
                    {(() => {
                        const groups = {};
                        (addons || [])
                            .filter(a => addonMatchesPhase(a, phaseLabel) && !isCommercialAddon(a) && !INV_UP_RE.test(String(a.itemName || '')))
                            .forEach(a => { const cat = a.category || 'General'; if (!groups[cat]) groups[cat] = []; groups[cat].push(a); });
                        const nodes = [];
                        Object.keys(groups).sort().forEach(cat => {
                            nodes.push(<MenuItem key={`h-${cat}`} disabled sx={{ fontWeight: 700, fontSize: '0.65rem', color: '#64748B', opacity: '1 !important', fontFamily: poppins }}>{cat}</MenuItem>);
                            groups[cat].forEach(a => {
                                const priceNum = parseFloat(String(a.price ?? '').replace(/[^\d.+]/g, ''));
                                const priceLabel = String(a.price ?? '').includes('+') ? a.price : (Number.isFinite(priceNum) ? priceNum.toLocaleString() : a.price);
                                const unitTag = a.unitType === 'PerPanel' ? ' /panel' : a.unitType === 'PerYear' ? ' /year' : a.unitType === 'PerItem' ? ' /item' : '';
                                nodes.push(<MenuItem key={a.addonId} value={a.addonId} sx={{ fontSize: '0.72rem', fontFamily: poppins }}>{a.itemName} — RM {priceLabel}{unitTag}</MenuItem>);
                            });
                        });
                        return nodes;
                    })()}
                </Select>
            </Box>
        </Box>
    );
}

/**
 * One interactive plan card. Holds LOCAL stepper overrides { panelIdx, battQty }
 * initialised from the ranked plan; the inverter stepper drives the card's
 * inverter-upgrade add-on ROWS (via onAddAddon/onRemoveAddon), NOT local state.
 * Rebuilds the full priced/saving shape via the parent's hydrateConfig on every
 * change — so Monthly Saving / Savings % / ROI / Cash / Installment all recompute
 * live through the SHARED engine (no local pricing math here). The price fields the
 * card reads (preRebateCash / investCash / govRebate / cc[term] / roiYears /
 * investRoi) are produced EXACTLY like Simple's option card.
 */
function PlanCard({ plan, sortedRows, batt16, hydrateConfig, promoGovRebate, promoBackupbox, phaseMode, isOpen, onToggle, scenariosFor, bill, t, poppins, lblSx, rm, buildPlanDraft, isSelected, optLetter, onTogglePlan, onDraftChange, onLiveChange, onViableChange, onOpenCompare, addons, phaseLabel, addonRows, onAddAddon, onUpdateAddon, onRemoveAddon, onToggleShortcut }) {
    const meta = plan.meta;
    const battUnit = Number(batt16?.kwhEach) || 16;
    const is3p = phaseMode === '3phase';

    // Support-angle / MPPT string sizing (Change 3) — derived from PHASE only (no
    // support-angle data table). Single phase: MAX 4 panels per MPPT string.
    // Three phase: MIN 5 panels per MPPT string. We surface the per-MPPT min & max
    // panel count for the live panel total as an informational range.
    const SP_MPPT_MAX = 4;   // single-phase: up to 4 panels per MPPT
    const TP_MPPT_MIN = 5;   // three-phase: at least 5 panels per MPPT

    // Initial panel index = the row whose panel count matches the ranked plan.
    const initPanelIdx = useMemo(() => {
        const i = sortedRows.findIndex(r => r.panels === plan.panels);
        return i >= 0 ? i : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortedRows, plan.panels]);
    const initBattQty = useMemo(() => {
        const tot = Number(plan.battKwhTotal) || 0;
        return battUnit > 0 ? Math.round(tot / battUnit) : 0;
    }, [plan.battKwhTotal, battUnit]);

    const [panelIdx, setPanelIdx] = useState(initPanelIdx);
    const [battQty, setBattQty] = useState(initBattQty);
    const [showAngle, setShowAngle] = useState(false);   // Support Angle / MPPT row

    // Re-sync when the underlying ranked plan changes (inputs / advanced settings).
    useEffect(() => { setPanelIdx(initPanelIdx); setBattQty(initBattQty); }, [initPanelIdx, initBattQty]);

    // Max battery: 3 of the 16kWh unit (same ceiling the bill sweep uses).
    const MAX_BATT = 3;

    const row = sortedRows[panelIdx] || sortedRows[initPanelIdx];
    // Live recompute through the SAME hydrator the ranking used. The inverter upgrade
    // is now driven by the inverter-upgrade ROWS in addonRows (3rd hydrateConfig arg
    // ignored), so effInvKw + price react to the rows like every other add-on.
    const live = useMemo(
        () => (row ? hydrateConfig(row, battQty, 0, addonRows) : null) || plan,
        [row, battQty, hydrateConfig, plan, addonRows]
    );

    // ── SAFETY GATE (mirrors Simple's per-option `viable`) ───────────────────
    // Re-implements residentialSolarCalculatorV2's option-card gate for the LIVE
    // (stepper-adjusted) config:
    //   sr         = computeSafetyRatio(kwp, effInvKw, hasBattery)  →  .ok = ratio < cap
    //                (cap = 1.42 no-battery / 1.8 with-battery, per the shared hook).
    //   overSeda   = effInvKw > SEDA cap (1P 5kW / 3P 15kW).
    //   cccBlocked = overSeda && no CCC add-on present. Advanced has NO per-card CCC
    //                add-on mechanism, so an over-SEDA config can never carry CCC here
    //                → it is always blocked (the faithful mirror of Simple when CCC is
    //                absent: removing/lacking CCC re-blocks the option, user 2026-06-01).
    //   viable     = sr.ok && !cccBlocked  — identical fold to Simple's `viable`.
    // When NOT viable the price/ROI tiles hide, a warning shows, and the checkbox is
    // disabled (an unviable plan can't be selected → never reaches Create-Sales).
    const effInvKw = Number(live.effInvKw) || Number(live.naturalInvKw) || 0;
    const sedaCap = is3p ? SEDA_CAP_3P : SEDA_CAP_1P;
    const sr = computeSafetyRatio(Number(live.kwp) || 0, effInvKw, Number(live.battQty) > 0);
    const overSeda = effInvKw > sedaCap;
    // CCC add-on unblocks an over-SEDA inverter (mirrors Simple, user 2026-06-21):
    // detect the CCC item in the catalog + this card's own add-on rows.
    const cccAddon = (addons || []).find(a => /\bccc\b/i.test(a.itemName || ''));
    const hasCcc = !!cccAddon && (addonRows || []).some(r => r.addonId === cccAddon.addonId);
    const cccBlocked = overSeda && !hasCcc;
    const viable = !!(sr && sr.ok) && !cccBlocked;

    // The live draft (built from THIS card's current steppers). Lift it up whenever
    // the card is selected so the bottom Create-Sales bar always matches the live
    // config (mirrors the old single-select lift, now per-selected-card).
    const liveDraft = useMemo(() => buildPlanDraft(live), [buildPlanDraft, live]);
    useEffect(() => {
        if (isSelected) onDraftChange(plan.key, liveDraft);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSelected, liveDraft, plan.key]);
    // Report this card's viability up so the parent prune effect drops an unviable key
    // from `selectedKeys`. If the live config just went unviable while selected,
    // proactively deselect it here too (belt-and-braces with the prune effect).
    useEffect(() => {
        onViableChange(plan.key, viable);
        if (!viable && isSelected) onTogglePlan(plan.key, liveDraft, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viable, plan.key, isSelected]);
    // Lift the LIVE (stepper-adjusted) display object so the 图表 comparison table can
    // read this card's current panels/kwp/battery/saving/cc/roi/inverter values.
    useEffect(() => {
        if (isSelected) onLiveChange(plan.key, live);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSelected, live, plan.key]);

    // ── Inverter stepper — now ADD/REMOVE inverter-upgrade add-on ROWS (mirrors
    //    Simple's stepTo/stepByFrom IIFE, residentialSolarCalculatorV2 ~L3693-3741).
    //    "+" finds the next chain step from the CURRENT effInvKw and onAddAddon()s its
    //    upgrade row (auto-adding CCC over SEDA); "−" removes the most-recently-added
    //    inverter-upgrade row. Caps: 1P max 10kW / 3P max 20kW. effInvKw / safety /
    //    price all react through the rows. ──
    const phaseDigit = is3p ? '3' : '1';
    const hardCap = is3p ? 20 : 10;
    const sedaCapFor = is3p ? 15 : 5;
    // Step map keyed by FROM-kW for the active phase, built from OUR catalog.
    const invStepByFromCard = useMemo(() => {
        const map = new Map();
        for (const a of (addons || [])) {
            const m = INV_UP_RE.exec(String(a.itemName || ''));
            if (!m) continue;
            const [, fp, fk, tp, tk] = m;
            if (fp !== phaseDigit || tp !== phaseDigit) continue;
            map.set(parseFloat(fk), { toKw: parseFloat(tk), addonId: a.addonId });
        }
        return map;
    }, [addons, phaseDigit]);
    const nextStep = invStepByFromCard.get(effInvKw);   // the step available from here
    // "+" enabled only if the next chain step exists AND stays within the hard cap.
    const canInvInc = !!nextStep && nextStep.toKw <= hardCap + 0.01;
    // "−" enabled only if at least one inverter-upgrade row is present on this card.
    const canInvDec = (addonRows || []).some(r => INV_UP_RE.test(String(r.itemName || '')));
    const invInc = () => {
        if (!nextStep) return;
        onAddAddon(nextStep.addonId);   // onAddAddon guards inverter-row duplicates
        // Auto-add CCC when the upgraded inverter goes over SEDA (mirrors Simple).
        if (nextStep.toKw > sedaCapFor && cccAddon && !hasCcc) onAddAddon(cccAddon.addonId);
    };
    const invDec = () => {
        // Remove the most-recently-added inverter-upgrade row (highest array index).
        let last = -1;
        for (let i = (addonRows || []).length - 1; i >= 0; i--) {
            if (INV_UP_RE.test(String(addonRows[i].itemName || ''))) { last = i; break; }
        }
        if (last < 0) return;
        // The row's FROM-kW is where the inverter lands after removal. If that's back
        // within SEDA, the auto-added CCC is no longer needed → remove it too (user
        // 2026-06-21: stepping 6kW→5kW must drop the CCC, not retain it).
        const m = INV_UP_RE.exec(String(addonRows[last].itemName || ''));
        const fromKw = m ? parseFloat(m[2]) : 0;
        onRemoveAddon(addonRows[last].rowId);
        if (fromKw <= sedaCapFor && cccAddon) {
            const cccRow = (addonRows || []).find(r => r.addonId === cccAddon.addonId);
            if (cccRow) onRemoveAddon(cccRow.rowId);
        }
    };

    // Reset the inverter to NATURAL whenever the panels/kWp change (user 2026-06-21):
    // strip the inverter-upgrade rows (+ the auto-CCC) so the agent re-decides for the
    // new kWp — add a battery (relaxes the ratio cap) OR upgrade the inverter. Firing on
    // panelIdx (not just naturalInvKw) means even a same-natural panel change reverts to
    // natural, so the ratio/safety state is re-shown and the choice is explicit.
    const prevPanelRef = useRef(panelIdx);
    useEffect(() => {
        if (prevPanelRef.current === panelIdx) return;
        prevPanelRef.current = panelIdx;
        const invRows = (addonRows || []).filter(r => INV_UP_RE.test(String(r.itemName || '')));
        if (!invRows.length) return;
        invRows.forEach(r => onRemoveAddon(r.rowId));
        if (cccAddon) {
            const cccRow = (addonRows || []).find(r => r.addonId === cccAddon.addonId);
            if (cccRow) onRemoveAddon(cccRow.rowId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [panelIdx]);

    const scenarios = isOpen ? scenariosFor(live) : [];
    // Display values — already computed Simple-style by hydrateConfig/priceLikeSimple.
    const govRebate = promoGovRebate ? (Number(live.govRebate) || 0) : 0;
    const quoteCash = Number(live.preRebateCash) || 0;     // the customer QUOTE (pre-rebate)
    const investCash = Number(live.investCash) || quoteCash; // after-rebate net investment
    const roiQuote = Number.isFinite(live.roiYears) ? live.roiYears : null;
    const roiInvest = Number.isFinite(live.investRoi) ? live.investRoi : roiQuote;
    const ccOf = (term) => (live.cc && live.cc[term] != null) ? live.cc[term] : 0;

    // ── Support Angle / MPPT string sizing (Change 3) ────────────────────
    // Given the live panel total, compute the MPPT-string arrangement range:
    //   Single phase → MAX 4 panels/string → strings ≥ ceil(panels/4); the
    //     min-per-string is the even spread floor(panels/strings).
    //   Three phase  → MIN 5 panels/string → strings ≤ floor(panels/5); the
    //     max-per-string is the even spread ceil(panels/strings).
    // We show "Min X / Max Y panels per MPPT" for the current panel count.
    const mppt = useMemo(() => {
        const panels = Number(live.panels) || 0;
        if (panels <= 0) return null;
        if (is3p) {
            // 3-phase: at least TP_MPPT_MIN per string → fewest viable strings.
            const strings = Math.max(1, Math.floor(panels / TP_MPPT_MIN));
            const minPer = TP_MPPT_MIN;                                   // floor per the rule
            const maxPer = Math.ceil(panels / strings);                   // even-spread ceiling
            return { strings, minPer, maxPer, ruleLabel: t(`三相：每 MPPT ≥ ${TP_MPPT_MIN} 块`, `Three-phase: ≥ ${TP_MPPT_MIN} panels per MPPT`, `3 Fasa: ≥ ${TP_MPPT_MIN} panel setiap MPPT`) };
        }
        // single-phase: at most SP_MPPT_MAX per string → most strings needed.
        const strings = Math.max(1, Math.ceil(panels / SP_MPPT_MAX));
        const maxPer = SP_MPPT_MAX;                                       // cap per the rule
        const minPer = Math.floor(panels / strings) || 1;                // even-spread floor
        return { strings, minPer, maxPer, ruleLabel: t(`单相：每 MPPT ≤ ${SP_MPPT_MAX} 块`, `Single-phase: ≤ ${SP_MPPT_MAX} panels per MPPT`, `1 Fasa: ≤ ${SP_MPPT_MAX} panel setiap MPPT`) };
    }, [live.panels, is3p, t]);

    return (
        <Box onClick={() => onTogglePlan(plan.key, liveDraft, viable)} sx={{
            p: 1.75, background: meta.bg, borderRadius: 3, display: 'flex', flexDirection: 'column',
            position: 'relative', cursor: viable ? 'pointer' : 'default',
            // Accent ring + lift when this card is the selected plan (Change 1).
            border: isSelected ? `2px solid ${meta.accent}` : `1px solid ${meta.bd}`,
            boxShadow: isSelected ? `0 0 0 3px ${meta.accent}22, 0 4px 14px ${meta.accent}33` : 'none',
            transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        }}>
            {/* Whole card toggles selection (user 2026-06-21) — the checkbox is too
                small. Interactive controls (steppers, buttons, expanded details) call
                stopPropagation below so they don't toggle. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: viable ? '#fff' : 'rgba(255,255,255,0.6)', fontFamily: poppins }}>
                    {t(plan.label.cn, plan.label.en, plan.label.my)}
                    {!viable && <span style={{ fontSize: '0.6rem', fontWeight: 800, color: '#FCA5A5', marginLeft: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>· {t('不可用', 'Blocked', 'Tidak tersedia')}</span>}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {/* "✓ Selected · Opt X" chip — Opt letter reflects selection order so
                        the export chooser's Opt A / Opt B map is obvious on the card. */}
                    {isSelected && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, px: 0.75, py: 0.15, borderRadius: 999, bgcolor: meta.accent }}>
                            <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#fff', fontFamily: poppins, letterSpacing: '0.03em' }}>
                                ✓ {t('已选', 'Selected', 'Dipilih')}{optLetter ? ` · Opt ${optLetter}` : ''}
                            </Typography>
                        </Box>
                    )}
                    {/* CHECKBOX multi-select (Change 1) — toggles this plan in selectedKeys.
                        Disabled when the live config isn't viable (safety gate) so an
                        unviable plan can never be ticked into the export / Create-Sales flow. */}
                    <Checkbox
                        size="small"
                        checked={isSelected}
                        disabled={!viable}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => onTogglePlan(plan.key, liveDraft, viable)}
                        sx={{ p: 0.25, color: meta.accent, '&.Mui-checked': { color: meta.accent }, '&.Mui-disabled': { color: '#CBD5E1' } }}
                        inputProps={{ 'aria-label': t('选用此方案', 'Select this plan', 'Pilih pelan ini') }}
                    />
                </Box>
            </Box>

            {/* System config (reflects live steppers) */}
            <Typography sx={{ fontFamily: poppins, fontSize: '0.95rem', fontWeight: 800, color: '#fff' }}>
                {live.kwp} kWp
            </Typography>
            <Typography sx={{ fontFamily: poppins, fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', mb: 1 }}>
                {live.panels} {t('块板', 'panels', 'panel')}
                {live.inverterLabel ? ` · ${live.effInvKw && live.naturalInvKw && live.effInvKw !== live.naturalInvKw ? `${live.effInvKw}kW` : live.inverterLabel}` : ''}
                {live.battQty > 0 ? ` · ${live.battQty}×${live.battKwhEach}kWh` : ` · ${t('无电池', 'No Battery', 'Tiada Bateri')}`}
            </Typography>

            {/* Safety ratio + status (user 2026-06-21) — like Simple's "1P 5kW · ratio 1.41 ·
                安全需电池". Tells the agent at a glance whether the config is safe, and why
                (over-SEDA needs CCC; over-cap needs a battery or a bigger inverter). */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Typography sx={{ fontSize: '0.66rem', fontFamily: "'JetBrains Mono',monospace", color: 'rgba(255,255,255,0.85)' }}>
                    {is3p ? '3P' : '1P'} {effInvKw || '—'}kW
                </Typography>
                {sr && (
                    <Typography sx={{ fontSize: '0.66rem', fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, color: sr.ok ? '#86EFAC' : '#FCA5A5' }}>
                        {t('比例', 'ratio', 'nisbah')} {Number(sr.ratio).toFixed(2)} · {sr.ok ? t('安全', 'SAFE', 'SELAMAT') : t('不安全', 'UNSAFE', 'TIDAK SELAMAT')}
                    </Typography>
                )}
                {overSeda && <Typography sx={{ fontSize: '0.6rem', fontFamily: poppins, fontWeight: 700, color: '#FDE68A' }}>· {t('超 SEDA 需 CCC', 'over SEDA · CCC', 'lebih SEDA · CCC')}</Typography>}
            </Box>

            {/* Steppers — Battery / Panels / Inverter (stopPropagation so +/- doesn't toggle the card) */}
            <Box onClick={e => e.stopPropagation()} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                <Stepper
                    label={t('电池', 'Battery', 'Bateri')}
                    value={battQty > 0 ? `${battQty}×${battUnit}kWh` : t('无', 'None', 'Tiada')}
                    onDec={() => setBattQty(q => Math.max(0, q - 1))}
                    onInc={() => setBattQty(q => Math.min(MAX_BATT, q + 1))}
                    canDec={battQty > 0} canInc={battQty < MAX_BATT && !!batt16}
                    accent={meta.accent} poppins={poppins}
                />
                <Stepper
                    label={t('板数', 'Panels', 'Panel')}
                    value={`${live.panels} (${live.kwp}kWp)`}
                    onDec={() => setPanelIdx(i => Math.max(0, i - 1))}
                    onInc={() => setPanelIdx(i => Math.min(sortedRows.length - 1, i + 1))}
                    canDec={panelIdx > 0} canInc={panelIdx < sortedRows.length - 1}
                    accent={meta.accent} poppins={poppins}
                />
                <Stepper
                    label={t('逆变器', 'Inverter', 'Inverter')}
                    value={`${live.effInvKw || '—'}kW`}
                    onDec={invDec}
                    onInc={invInc}
                    canDec={canInvDec} canInc={canInvInc}
                    accent={meta.accent} poppins={poppins}
                />
            </Box>

            {/* Support Angle / MPPT string sizing (Change 3) — informational,
                derived from phase. Expandable to keep the card compact. */}
            {mppt && (
                <Box onClick={e => e.stopPropagation()} sx={{ mb: 1 }}>
                    <Button size="small" onClick={() => setShowAngle(v => !v)}
                        endIcon={showAngle ? <ExpandLessIcon sx={{ fontSize: '0.9rem' }} /> : <ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}
                        sx={{ width: '100%', textTransform: 'none', fontFamily: poppins, fontWeight: 700, fontSize: '0.62rem', color: 'rgba(255,255,255,0.7)', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2, px: 1, py: 0.4 }}>
                        {t('支撑角度 / MPPT', 'Support Angle / MPPT', 'Sudut Sokongan / MPPT')}
                    </Button>
                    <Collapse in={showAngle}>
                        <Box sx={{ mt: 0.5, p: 0.75, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2 }}>
                            <Typography sx={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.6)', fontFamily: poppins, mb: 0.5 }}>
                                {mppt.ruleLabel} · {live.panels} {t('块板', 'panels', 'panel')} · {mppt.strings} {t('组串', mppt.strings === 1 ? 'string' : 'strings', mppt.strings === 1 ? 'rentetan' : 'rentetan')}
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                                <Box sx={{ p: 0.6, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2, textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: '0.52rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', fontFamily: poppins }}>{t('每串最少', 'Min per MPPT', 'Min/MPPT')}</Typography>
                                    <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.78rem', fontWeight: 800, color: '#fff' }}>{mppt.minPer}</Typography>
                                </Box>
                                <Box sx={{ p: 0.6, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2, textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: '0.52rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', fontFamily: poppins }}>{t('每串最多', 'Max per MPPT', 'Maks/MPPT')}</Typography>
                                    <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.78rem', fontWeight: 800, color: '#fff' }}>{mppt.maxPer}</Typography>
                                </Box>
                            </Box>
                            <Typography sx={{ fontSize: '0.52rem', color: 'rgba(255,255,255,0.5)', fontFamily: poppins, mt: 0.5, fontStyle: 'italic' }}>
                                {t('依电表相位推算（无支撑角度数据表）', 'Derived from meter phase (no support-angle data table)', 'Dianggar dari fasa meter (tiada jadual sudut sokongan)')}
                            </Typography>
                        </Box>
                    </Collapse>
                </Box>
            )}

            {/* SAFETY-GATE warning (mirrors Simple's blocked-state placeholder). When
                the live config breaches the ratio cap or exceeds SEDA, hide all price /
                ROI tiles and show this banner instead. Sales must fix via the steppers
                above (add battery / upgrade inverter / fewer panels) before a price shows. */}
            {!viable && (() => {
                // Two DISTINCT cases, mirroring Simple mode:
                //  • SEDA-only (overSeda && ratio OK): the inverter is already big enough
                //    for the ratio — it only needs CCC registration. Show ONLY "Add CCC"
                //    (adding a battery / upgrading would be wrong / double-charge).
                //  • Ratio breach (ratio NOT ok): needs a battery (relaxes 1.42→1.8) or a
                //    bigger inverter. Show "Add Battery" / "Upgrade Inverter" (NOT CCC).
                const sedaOnly = overSeda && !!(sr && sr.ok);
                const hasBatt = Number(live.battQty) > 0;
                const cccPrice = cccAddon ? (computeAddonAmount(cccAddon, 1).total || 0) : 0;
                const fixBtnSx = { fontFamily: poppins, fontSize: '0.66rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', py: 0.2, px: 1.25, lineHeight: 1.3, color: '#FECACA', borderColor: 'rgba(252,165,165,0.5)', '&:hover': { borderColor: '#FCA5A5', bgcolor: 'rgba(252,165,165,0.12)' } };
                return (
                    <Box sx={{ p: 1.25, bgcolor: 'rgba(220,38,38,0.12)', border: '1px dashed rgba(248,113,113,0.4)', borderRadius: 2, mb: 1, textAlign: 'center' }}>
                        {sedaOnly ? (<>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#FCA5A5', fontFamily: poppins, mb: 0.4 }}>
                                {t('逆变器需注册 CCC', 'Inverter needs CCC', 'Inverter perlu CCC')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.62rem', color: 'rgba(252,165,165,0.85)', fontFamily: poppins, lineHeight: 1.4 }}>
                                {t(`SEDA 最高 ${sedaCap}kW — ${effInvKw}kW 超出，需添加 CCC 注册`, `SEDA max ${sedaCap}kW — ${effInvKw}kW exceeds, add CCC to register`, `SEDA maks ${sedaCap}kW — ${effInvKw}kW melebihi, tambah CCC untuk daftar`)}
                            </Typography>
                            {cccAddon && !hasCcc && (
                                <Box onClick={e => e.stopPropagation()} sx={{ display: 'flex', justifyContent: 'center', mt: 0.85 }}>
                                    <Button size="small" variant="outlined" onClick={() => onAddAddon(cccAddon.addonId)} sx={fixBtnSx}>
                                        {'+ '}{t(`添加 CCC RM${cccPrice.toLocaleString()}`, `Add CCC RM${cccPrice.toLocaleString()}`, `Tambah CCC RM${cccPrice.toLocaleString()}`)}
                                    </Button>
                                </Box>
                            )}
                        </>) : (<>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#FCA5A5', fontFamily: poppins, mb: 0.4 }}>
                                {t('当前配置超出安全比例', 'Config breaches safety ratio', 'Konfigurasi melebihi nisbah keselamatan')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.62rem', color: 'rgba(252,165,165,0.85)', fontFamily: poppins, lineHeight: 1.4 }}>
                                {hasBatt
                                    ? t('比例超过 1.8 — 需升级逆变器或减少电板', 'Ratio exceeds 1.8 — needs a bigger inverter or fewer panels', 'Nisbah > 1.8 — perlu inverter lebih besar atau kurang panel')
                                    : t('需要电池或升级逆变器（无电池上限 1.42）', 'Needs a battery or a bigger inverter (cap 1.42 without)', 'Perlu bateri atau inverter lebih besar (had 1.42 tanpa)')}
                            </Typography>
                            <Box onClick={e => e.stopPropagation()} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center', mt: 0.85 }}>
                                {!hasBatt && battQty < MAX_BATT && !!batt16 && (
                                    <Button size="small" variant="outlined" onClick={() => setBattQty(q => Math.min(MAX_BATT, q + 1))} sx={fixBtnSx}>
                                        {t('加电池', 'Add Battery', 'Tambah Bateri')}
                                    </Button>
                                )}
                                {canInvInc && (
                                    <Button size="small" variant="outlined" onClick={invInc} sx={fixBtnSx}>
                                        {t('升级逆变器', 'Upgrade Inverter', 'Naik Taraf Inverter')}
                                    </Button>
                                )}
                            </Box>
                        </>)}
                    </Box>
                );
            })()}

            {/* Saving + ROI + ALL price tiles (live) — hidden while the config isn't
                viable (safety gate). Mirrors Simple, which hides the bill/price/ROI
                sections behind the same `viable` flag. */}
            {viable && (<>
            <Box sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2, mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontFamily: poppins, fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)' }}>
                    <span>{t('每月节省', 'Monthly Saving', 'Jimat Bulanan')}</span>
                    <span style={{ fontWeight: 800, color: '#86EFAC' }}>{rm(live.monthlySaving)}</span>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontFamily: poppins, fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)' }}>
                    <span>{t('每年节省', 'Yearly Saving', 'Jimat Tahunan')}</span>
                    <span style={{ fontWeight: 700, color: '#fff' }}>{rm(live.yearlySaving)}</span>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontFamily: poppins, fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)' }}>
                    <span>{t('回本 ROI', 'ROI', 'ROI')}</span>
                    <span style={{ fontWeight: 800, color: '#93C5FD' }}>{roiQuote != null ? roiQuote : '—'} {t('年', 'yrs', 'thn')}</span>
                </Box>
                {govRebate > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontFamily: poppins, fontSize: '0.68rem', color: '#86EFAC' }}>
                        <span>{t('投资回本（含回扣）', 'Net ROI (after rebate)', 'ROI Bersih (selepas rebat)')}</span>
                        <span style={{ fontWeight: 700 }}>{roiInvest != null ? roiInvest : '—'} {t('年', 'yrs', 'thn')}</span>
                    </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontFamily: poppins, fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)' }}>
                    <span>{t('电费减少', 'Bill Cut', 'Pengurangan Bil')}</span>
                    <span style={{ fontWeight: 700, color: '#fff' }}>{live.savingsPct}%</span>
                </Box>
                {/* 10-year NET saving = monthlySaving × 120 − system cash price (mirrors
                    the 10-Year modal's cash row). Shown alongside monthly/yearly. */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontFamily: poppins, fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', borderTop: '1px solid rgba(255,255,255,0.12)', mt: 0.4, pt: 0.4 }}>
                    <span>{t('10年净省', '10-Yr Net Saving', 'Jimat Bersih 10thn')}</span>
                    <span style={{ fontWeight: 800, color: '#86EFAC' }}>{rm(Math.max(0, (Number(live.monthlySaving) || 0) * 120 - quoteCash))}</span>
                </Box>
            </Box>

            {/* Pricing — Early Bird 0% Installment (36mo + /mo), Cash, optional Rebate.
                Mirrors Simple's option card: headline Cash + CC convert the PRE-rebate
                quote; the SuRIA "investment" (after-rebate) line shows beneath. */}
            <Box sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2, mb: 1, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: '#93C5FD', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: poppins }}>{t('早鸟 0% 分期 · 36 期', 'Early Bird 0% · 36 mo', 'Ansuran 0% · 36 bln')}</Typography>
                <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '1.1rem', fontWeight: 800, color: '#fff' }}>{rm(ccOf(36))}</Typography>
                <Typography sx={{ fontSize: '0.64rem', color: 'rgba(255,255,255,0.7)', fontFamily: poppins }}>{rm(ccOf(36) / 36)} / {t('月', 'mo', 'bln')}</Typography>
                {govRebate > 0 && <Typography sx={{ fontSize: '0.58rem', color: '#86EFAC', fontFamily: poppins, mt: 0.1 }}>{t('投资', 'Net', 'Bersih')} {rm(ccOf(36) - govRebate)}</Typography>}
            </Box>
            <Box sx={{ p: 1, bgcolor: 'rgba(134,239,172,0.12)', border: '1px solid rgba(134,239,172,0.35)', borderRadius: 2, mb: 1, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: '#86EFAC', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: poppins }}>{t('现金价', 'Cash Price', 'Harga Tunai')}</Typography>
                <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '1.2rem', fontWeight: 800, color: '#86EFAC' }}>{rm(quoteCash)}</Typography>
                {govRebate > 0 && (
                    <Typography sx={{ fontSize: '0.6rem', color: 'rgba(134,239,172,0.85)', fontFamily: poppins }}>
                        {t('投资（含回扣）', 'Net investment', 'Pelaburan bersih')} {rm(investCash)} <span style={{ opacity: 0.7 }}>({rm(quoteCash)} − {rm(govRebate)})</span>
                    </Typography>
                )}
            </Box>
            {/* CC tiles (all terms) — now SHOWN with the price block (user 2026-06-21),
                like Simple. Convert the PRE-rebate quote; SuRIA net shows beneath. */}
            <Typography sx={{ ...lblSx, color: 'rgba(255,255,255,0.7)', mb: 0.5 }}>{t('信用卡分期 (CC)', 'Credit Card (CC)', 'Kad Kredit (CC)')}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mb: 1 }}>
                {CC_TERMS.map(term => (
                    <Box key={term} sx={{ p: 0.5, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)', fontFamily: poppins }}>{term} {t('期', 'mo', 'bln')}</Typography>
                        <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.82rem', fontWeight: 800, color: '#93C5FD' }}>{rm(ccOf(term))}</Typography>
                        {govRebate > 0 && <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.56rem', fontWeight: 600, color: '#86EFAC' }}>{t('投资', 'Net', 'Bersih')} {rm(ccOf(term) - govRebate)}</Typography>}
                    </Box>
                ))}
            </Box>
            {/* addOnRm now INCLUDES the inverter-upgrade row's cost (it's a regular add-on
                row), so show one combined "Incl. add-ons" total — no separate inverter
                line (that would double-count). */}
            {live.addOnRm > 0 && (
                <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.6)', fontFamily: poppins, mb: 1 }}>
                    {t('含加建', 'Incl. add-ons', 'Termasuk tambahan')}: {t('配件', 'Add-ons', 'Tambahan')} {rm(live.addOnRm)}
                </Typography>
            )}
            </>)}

            {/* Select toggle removed (user 2026-06-21) — the header checkbox is the only
                selection control; the "选用此方案" button was redundant. */}

            {/* Per-card add-ons — ALWAYS visible now (user 2026-06-21), not tucked inside
                Show Details. THIS plan's own editable rows; PerPanel items price by this
                plan's panel count. Folds into the card price + Create Sales. */}
            <Box onClick={e => e.stopPropagation()} sx={{ cursor: 'default', mb: 1 }}>
                <Typography sx={{ ...lblSx, color: 'rgba(255,255,255,0.7)', mb: 0.5 }}>🧰 {t('配件包', 'Add-on Package', 'Pakej Tambahan')}</Typography>
                {/* Inverter-upgrade read-only marker removed (user 2026-06-21) — the
                    inverter upgrade is now a REAL editable row in AddonRowsEditor below
                    (driven by the Inverter stepper, which adds/removes its add-on row),
                    so the redundant marker is gone. The backup-box marker is kept. */}
                {/* Backup-box removal marker (user 2026-06-21) — like Simple's qty-0 synth
                    row. Backup-box is INCLUDED in the ATAP base; when the no-battery promo
                    removes it, show it as a qty-0 line (the −RM saving is in the price). */}
                {promoBackupbox && Number(live.battQty) === 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 0.75, mb: 0.5, bgcolor: 'rgba(148,163,184,0.12)', border: '1px dashed rgba(148,163,184,0.4)', borderRadius: 2 }}>
                        <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: 'rgba(255,255,255,0.75)', fontFamily: poppins }}>
                            {is3p ? t('三相 Backup-Box（已移除）', 'Three Phase Backup-Box (Removed)', 'Backup-Box 3 Fasa (Dibuang)') : t('Backup-Box（已移除）', 'Backup-Box (Removed)', 'Backup-Box (Dibuang)')}
                            <span style={{ color: 'rgba(255,255,255,0.45)', marginLeft: 6 }}>· {t('数量', 'Qty', 'Kuan')} 0</span>
                        </Typography>
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#86EFAC', fontFamily: "'JetBrains Mono',monospace" }}>−RM {(is3p ? 2500 : 1600).toLocaleString()}</Typography>
                    </Box>
                )}
                <AddonRowsEditor
                    rows={addonRows}
                    addons={addons}
                    phaseLabel={phaseLabel}
                    previewPanels={Number(live.panels) || 0}
                    poppins={poppins}
                    t={t}
                    onAdd={onAddAddon}
                    onUpdate={onUpdateAddon}
                    onRemove={onRemoveAddon}
                    onToggleShortcut={onToggleShortcut}
                />
            </Box>

            {/* Per-card 10-Year Comparison link (Change 2) — opens the modal focused
                on THIS plan only. */}
            <Button size="small" onClick={(e) => { e.stopPropagation(); onOpenCompare(); }}
                sx={{ mt: 'auto', mb: 1, textTransform: 'none', fontFamily: poppins, fontWeight: 700, fontSize: '0.64rem', color: 'rgba(255,255,255,0.7)', justifyContent: 'center', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: '#fff' } }}>
                {t('查看10年对比', 'View 10-Year Comparison', 'Lihat Bandingan 10 Tahun')}
            </Button>

            {/* Expandable details */}
            <Button size="small" onClick={(e) => { e.stopPropagation(); onToggle(); }}
                endIcon={isOpen ? <ExpandLessIcon sx={{ fontSize: '1rem' }} /> : <ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
                sx={{ textTransform: 'none', fontFamily: poppins, fontWeight: 700, fontSize: '0.66rem', color: '#93C5FD', justifyContent: 'space-between' }}>
                {isOpen ? t('隐藏详情', 'Hide Details', 'Sembunyi Butiran') : t('显示详情', 'Show Details', 'Tunjuk Butiran')}
            </Button>
            <Collapse in={isOpen}>
                <Box onClick={e => e.stopPropagation()} sx={{ mt: 0.75, cursor: 'default' }}>
                    {/* Energy metrics */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0.5, mb: 1 }}>
                        {[
                            [t('新进口', 'New Import', 'Import Baru'), `${live.flow.newImport.toLocaleString()} kWh`],
                            [t('新出口', 'New Export', 'Eksport Baru'), `${live.flow.exportKwh.toLocaleString()} kWh`],
                            [t('电池利用', 'Battery Util.', 'Guna Bateri'), live.flow.batteryUtil != null ? `${live.flow.batteryUtil}%` : 'N/A'],
                        ].map(([lbl, val]) => (
                            <Box key={lbl} sx={{ p: 0.75, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '0.54rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', fontFamily: poppins }}>{lbl}</Typography>
                                <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.72rem', fontWeight: 700, color: '#fff' }}>{val}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Inverter info */}
                    {live.inverterLabel && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 0.75, bgcolor: 'rgba(253,230,138,0.12)', border: '1px solid rgba(253,230,138,0.35)', borderRadius: 2, mb: 1 }}>
                            <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#FDE68A', fontFamily: poppins }}>⚡ {t('逆变器', 'Inverter', 'Penyongsang')}</Typography>
                            <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#fff', fontFamily: poppins }}>
                                {live.effInvKw && live.naturalInvKw && live.effInvKw !== live.naturalInvKw ? `${live.naturalInvKw}kW → ${live.effInvKw}kW` : live.inverterLabel}
                            </Typography>
                        </Box>
                    )}

                    {/* Bill scenario table */}
                    {scenarios.length > 0 && (
                        <>
                            <Typography sx={{ ...lblSx, color: 'rgba(255,255,255,0.7)', mb: 0.5 }}>📊 {t('电费情境分析', 'Scenario Analysis', 'Analisis Senario')}</Typography>
                            <Box sx={{ border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2, overflow: 'hidden' }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 0.6fr', bgcolor: 'rgba(255,255,255,0.1)', px: 0.75, py: 0.4 }}>
                                    {[t('电费情境', 'Bill', 'Bil'), t('预估节省', 'Saving', 'Jimat'), t('比例', '%', '%')].map((h, i) => (
                                        <Typography key={i} sx={{ fontSize: '0.54rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', fontFamily: poppins, textAlign: i === 0 ? 'left' : 'right' }}>{h}</Typography>
                                    ))}
                                </Box>
                                {scenarios.map((s, i) => {
                                    const isCurrent = Math.abs(s.bill - bill) < 1;
                                    return (
                                        <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 0.6fr', px: 0.75, py: 0.3, bgcolor: isCurrent ? 'rgba(255,255,255,0.12)' : 'transparent', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                            <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.64rem', fontWeight: isCurrent ? 800 : 500, color: '#fff' }}>{rm(s.bill)}</Typography>
                                            <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.64rem', color: '#86EFAC', textAlign: 'right' }}>{rm(s.saving)}</Typography>
                                            <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.64rem', color: 'rgba(255,255,255,0.6)', textAlign: 'right' }}>{s.pct.toFixed(0)}%</Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </>
                    )}

                </Box>
            </Collapse>
        </Box>
    );
}
